import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createClient } from "@supabase/supabase-js";
import {
  RecertSuspendedScreen,
  RecertWarningBanner,
  RecertManagementPage,
  getRecertStatusInfo,
} from "./RecertComponents";
import {
  EngagementPage,
  CustomerModal,
  downloadCustomerTemplate,
  downloadEngagementTemplate,
} from "./EngagementComponents";

// ============================================================
// SUPABASE CONFIG — Replace with your project values
// Found in: Supabase Dashboard > Settings > API
// ============================================================
const SUPABASE_URL = "https://yzvkhbsjxjwqhssqqrhb.supabase.co";
const SUPABASE_ANON_KEY =  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl6dmtoYnNqeGp3cWhzc3FxcmhiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzIyNzcwMDQsImV4cCI6MjA4Nzg1MzAwNH0.0oY3g2Prb2OhojBvFHb-6X-O6hzTZP_BDU-LOh19F7I";

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: "pkce",          // Secure PKCE flow for browser
  }
});
window.__supabase = supabase; // Used by RecertComponents

// ============================================================
// SCHEMA DEFINITION
// ============================================================
const SCHEMA = {
  customers: {
    label: "Customers", icon: "🏢", color: "#6366f1", idField: "customer_id", idPrefix: "CMI",
    table: "customers", nameField: "customer_name",
    fields: [
      { key: "customer_id", label: "Customer ID", type: "text", readonly: true },
      { key: "customer_name", label: "Customer Name", type: "text", required: true },
      { key: "erp_platform", label: "ERP Platform", type: "select", options: ["Banner","Colleague","Both","Unknown"] },
      { key: "environment_type", label: "Environment Type", type: "select", options: ["Prod","Non-Prod","Mixed","Unknown"] },
      { key: "hosting_model", label: "Hosting Model", type: "text" },
      { key: "region", label: "Region", type: "text" },
      { key: "primary_technical_owner", label: "Primary Technical Owner", type: "text" },
      { key: "active", label: "Active", type: "select", options: ["Yes","No"] },
      { key: "domain", label: "Domain", type: "text" },
      { key: "notes", label: "Notes", type: "textarea" },
    ]
  },
  servers: {
    label: "Servers", icon: "🖥️", color: "#0ea5e9", idField: "server_id", idPrefix: "SRV",
    table: "servers", nameField: "server_name",
    fields: [
      { key: "server_id", label: "Server ID", type: "text", readonly: true },
      { key: "customer_id", label: "Customer ID", type: "text", required: true },
      { key: "server_name", label: "Server Name", type: "text", required: true },
      { key: "os", label: "OS", type: "select", options: ["Windows","Linux","Unknown"] },
      { key: "environment", label: "Environment", type: "select", options: ["Prod","Test","Dev","Unknown"] },
      { key: "erp_association", label: "ERP Association", type: "select", options: ["Banner","Colleague","Shared","Unknown"] },
      { key: "criticality_tier", label: "Criticality Tier", type: "select", options: ["1","2","3","4","Unknown"] },
      { key: "monitoring_enabled", label: "Monitoring Enabled", type: "select", options: ["Y","N","Unknown"] },
      { key: "monitoring_tool", label: "Monitoring Tool", type: "text" },
      { key: "crowdstrike_enabled", label: "CrowdStrike Enabled", type: "select", options: ["Y","N","Unknown"] },
      { key: "local_ip", label: "Local IP", type: "text" },
      { key: "os_version", label: "OS Version", type: "text" },
      { key: "management_scope", label: "Management Scope", type: "select", options: ["Full","Application","Database","Read-Only","None"] },
      { key: "managed_by_non_ems", label: "Managed By (Non-EMS Infrastructure Services)", type: "text" },
      { key: "source", label: "Source", type: "text" },
      { key: "last_updated", label: "Last Updated", type: "date" },
      { key: "notes", label: "Notes", type: "textarea" },
    ]
  },
  databases: {
    label: "Databases", icon: "🗄️", color: "#10b981", idField: "database_id", idPrefix: "DB",
    table: "databases", nameField: "database_name",
    fields: [
      { key: "database_id", label: "Database ID", type: "text", readonly: true },
      { key: "customer_id", label: "Customer ID", type: "text", required: true },
      { key: "database_name", label: "Database Name", type: "text", required: true },
      { key: "erp_platform", label: "ERP Platform", type: "select", options: ["Banner","Colleague","Unknown"] },
      { key: "db_engine", label: "DB Engine", type: "select", options: ["SQL","Oracle","Postgres","MySQL","Other","Unknown"] },
      { key: "environment", label: "Environment", type: "select", options: ["Prod","Test","Dev","Unknown"] },
      { key: "ha_enabled", label: "HA Enabled", type: "select", options: ["Y","N","Unknown"] },
      { key: "backup_status", label: "Backup Status", type: "select", options: ["OK","Issue","Unknown"] },
      { key: "dr_enabled", label: "DR Enabled", type: "select", options: ["Y","N","Unknown"] },
      { key: "management_scope", label: "Management Scope", type: "select", options: ["Full","Application","Database","Read-Only","None"] },
      { key: "managed_by_non_ems", label: "Managed By (Non-EMS Infrastructure Services)", type: "text" },
      { key: "source", label: "Source", type: "text" },
      { key: "last_updated", label: "Last Updated", type: "date" },
      { key: "notes", label: "Notes", type: "textarea" },
    ]
  },
  endpoints: {
    label: "Endpoints", icon: "🛡️", color: "#f59e0b", idField: "endpoint_id", idPrefix: "EP",
    table: "endpoints", nameField: "endpoint_name",
    fields: [
      { key: "endpoint_id", label: "Endpoint ID", type: "text", readonly: true },
      { key: "customer_id", label: "Customer ID", type: "text", required: true },
      { key: "endpoint_name", label: "Endpoint Name", type: "text", required: true },
      { key: "endpoint_type", label: "Endpoint Type", type: "select", options: ["Server","Workstation","Other","Unknown"] },
      { key: "os", label: "OS", type: "text" },
      { key: "crowdstrike_installed", label: "CrowdStrike Installed", type: "select", options: ["Y","N"] },
      { key: "last_check_in", label: "Last Check In", type: "date" },
      { key: "alert_triggered", label: "Alert Triggered", type: "select", options: ["Y","N","Unknown"] },
      { key: "escalated_to_ems", label: "Escalated to EMS", type: "select", options: ["Y","N","Unknown"] },
      { key: "crowdstrike_host_id", label: "CrowdStrike Host ID", type: "text" },
      { key: "local_ip", label: "Local IP", type: "text" },
      { key: "platform", label: "Platform", type: "text" },
      { key: "os_version", label: "OS Version", type: "text" },
      { key: "source", label: "Source", type: "text" },
      { key: "import_status", label: "Import Status", type: "select", options: ["active","staged"] },
      { key: "last_updated", label: "Last Updated", type: "date" },
      { key: "notes", label: "Notes", type: "textarea" },
    ]
  },
  monitoring: {
    label: "Monitoring", icon: "📡", color: "#ec4899", idField: "node_id", idPrefix: "MON",
    table: "monitoring_nodes", nameField: "node_name",
    fields: [
      { key: "node_id", label: "Node ID", type: "text", readonly: true },
      { key: "customer_id", label: "Customer ID", type: "text", required: true },
      { key: "node_name", label: "Node Name", type: "text", required: true },
      { key: "node_type", label: "Node Type", type: "select", options: ["Server","DB","Network","Other","Unknown"] },
      { key: "monitored", label: "Monitored", type: "select", options: ["Y","N"] },
      { key: "monitoring_tool", label: "Monitoring Tool", type: "text" },
      { key: "sla_tier", label: "SLA Tier", type: "text" },
      { key: "last_seen", label: "Last Seen", type: "date" },
      { key: "source", label: "Source", type: "text" },
      { key: "import_status", label: "Import Status", type: "select", options: ["active","staged"] },
      { key: "last_updated", label: "Last Updated", type: "date" },
      { key: "notes", label: "Notes", type: "textarea" },
    ]
  },
  network: {
    label: "Network Assets", icon: "🔗", color: "#8b5cf6", idField: "device_id", idPrefix: "NW",
    table: "network_assets", nameField: "device_name",
    fields: [
      { key: "device_id", label: "Device ID", type: "text", readonly: true },
      { key: "customer_id", label: "Customer ID", type: "text", required: true },
      { key: "device_name", label: "Device Name", type: "text", required: true },
      { key: "device_type", label: "Device Type", type: "select", options: ["Firewall","Switch","Router","VPN","Other"] },
      { key: "remote_managed", label: "Remote Managed", type: "select", options: ["Y","N","Unknown"] },
      { key: "monitoring_enabled", label: "Monitoring Enabled", type: "select", options: ["Y","N","Unknown"] },
      { key: "security_scanned", label: "Security Scanned", type: "select", options: ["Y","N","Unknown"] },
      { key: "source", label: "Source", type: "text" },
      { key: "last_updated", label: "Last Updated", type: "date" },
      { key: "notes", label: "Notes", type: "textarea" },
    ]
  },
  middleware: {
    label: "Middleware", icon: "⚙️", color: "#f97316", idField: "middleware_id", idPrefix: "MW",
    table: "middleware", nameField: "middleware_name",
    fields: [
      { key: "middleware_id",   label: "Middleware ID",   type: "text", readonly: true },
      { key: "customer_id",    label: "Customer ID",     type: "text", required: true },
      { key: "server_id",      label: "Server",          type: "asset-link", assetType: "servers",  displayField: "server_name" },
      { key: "middleware_name",label: "Instance Name",   type: "text", required: true },
      { key: "middleware_type",label: "Type",            type: "select", options: ["Tomcat","IIS","Apache HTTP","ActiveMQ","Oracle Application Server","Other"] },
      { key: "version",        label: "Version",         type: "text" },
      { key: "port",           label: "Port",            type: "text" },
      { key: "environment",    label: "Environment",     type: "select", options: ["Prod","Test","Dev","Unknown"] },
      { key: "status",         label: "Status",          type: "select", options: ["Running","Stopped","Unknown"] },
      { key: "management_scope",label:"Management Scope",type: "select", options: ["Full","Application","Database","Read-Only","None"] },
      { key: "source",         label: "Source",          type: "text" },
      { key: "last_updated",   label: "Last Updated",    type: "date" },
      { key: "notes",          label: "Notes",           type: "textarea" },
    ]
  },
  applications: {
    label: "Applications", icon: "📦", color: "#14b8a6", idField: "application_id", idPrefix: "APP",
    table: "applications", nameField: "application_name",
    fields: [
      { key: "application_id",       label: "Application ID",    type: "text", readonly: true },
      { key: "customer_id",          label: "Customer ID",       type: "text", required: true },
      { key: "application_name",     label: "Application",       type: "select", options: ["Banner","Colleague","Workflow","Self-Service","Luminis","Degree Works","Ethos","Ellucian Experience","Other"] },
      { key: "application_name_other",label:"Application (Other)",type:"text" },
      { key: "version",              label: "Version",           type: "text" },
      { key: "environment",          label: "Environment",       type: "select", options: ["Prod","Test","Dev","Unknown"] },
      { key: "server_id",            label: "Server",            type: "asset-link", assetType: "servers",     displayField: "server_name" },
      { key: "middleware_id",        label: "Middleware",        type: "asset-link", assetType: "middleware",  displayField: "middleware_name" },
      { key: "database_id",          label: "Database",          type: "asset-link", assetType: "databases",   displayField: "database_name" },
      { key: "management_scope",     label: "Management Scope",  type: "select", options: ["Full","Application","Database","Read-Only","None"] },
      { key: "status",               label: "Status",            type: "select", options: ["Active","Inactive","Unknown"] },
      { key: "source",               label: "Source",            type: "text" },
      { key: "last_updated",         label: "Last Updated",      type: "date" },
      { key: "notes",                label: "Notes",             type: "textarea" },
    ]
  }
};

const EXCEL_COLUMN_MAP = {
  customers: { "Customer_ID":"customer_id","Customer_Name":"customer_name","ERP_Platform (Banner/Colleague/Both/Unknown)":"erp_platform","Environment_Type (Prod/Non-Prod/Mixed/Unknown)":"environment_type","Hosting_Model":"hosting_model","Region":"region","Primary_Technical_Owner":"primary_technical_owner","Active (Yes/No)":"active","Notes":"notes","Domain":"domain" },
  servers: { "Server_ID":"server_id","Customer_ID":"customer_id","Server_Name":"server_name","OS (Windows/Linux)":"os","Environment (Prod/Test/Dev/Unknown)":"environment","ERP_Association (Banner/Colleague/Shared/Unknown)":"erp_association","Criticality_Tier (1-4/Unknown)":"criticality_tier","Monitoring_Enabled (Y/N)":"monitoring_enabled","Monitoring_Tool":"monitoring_tool","CrowdStrike_Enabled (Y/N)":"crowdstrike_enabled","Local_IP":"local_ip","OS_Version":"os_version","Source":"source","Last_Updated":"last_updated","Notes":"notes","Management_Scope (Full/Application/Database/Read-Only/None)":"management_scope","Managed_By_Non_EMS":"managed_by_non_ems" },
  databases: { "Database_ID":"database_id","Customer_ID":"customer_id","Database_Name":"database_name","ERP_Platform (Banner/Colleague/Unknown)":"erp_platform","DB_Engine (SQL/Oracle/Postgres/MySQL/Other/Unknown)":"db_engine","Environment (Prod/Test/Dev/Unknown)":"environment","HA_Enabled (Y/N/Unknown)":"ha_enabled","Backup_Status (OK/Issue/Unknown)":"backup_status","DR_Enabled (Y/N/Unknown)":"dr_enabled","Source":"source","Last_Updated":"last_updated","Notes":"notes","Management_Scope (Full/Application/Database/Read-Only/None)":"management_scope","Managed_By_Non_EMS":"managed_by_non_ems" },
  endpoints: { "Endpoint_ID":"endpoint_id","Customer_ID":"customer_id","Endpoint_Name":"endpoint_name","Endpoint_Type (Server/Workstation/Other/Unknown)":"endpoint_type","OS":"os","CrowdStrike_Installed (Y/N)":"crowdstrike_installed","Last_Check_In":"last_check_in","Alert_Triggered (Y/N/Unknown)":"alert_triggered","Escalated_to_EMS (Y/N/Unknown)":"escalated_to_ems","CrowdStrike_Host_ID":"crowdstrike_host_id","Local_IP":"local_ip","Platform":"platform","OS_Version":"os_version","Source":"source","Last_Updated":"last_updated","Notes":"notes" },
  monitoring: { "Node_ID":"node_id","Customer_ID":"customer_id","Node_Name":"node_name","Node_Type (Server/DB/Network/Other/Unknown)":"node_type","Monitored (Y/N)":"monitored","Monitoring_Tool":"monitoring_tool","SLA_Tier":"sla_tier","Last_Seen":"last_seen","Source":"source","Last_Updated":"last_updated","Notes":"notes" },
  network: { "Device_ID":"device_id","Customer_ID":"customer_id","Device_Name":"device_name","Device_Type (Firewall/Switch/Router/VPN/Other)":"device_type","Remote_Managed (Y/N/Unknown)":"remote_managed","Monitoring_Enabled (Y/N/Unknown)":"monitoring_enabled","Security_Scanned (Y/N/Unknown)":"security_scanned","Source":"source","Last_Updated":"last_updated","Notes":"notes" },
  middleware: { "Middleware_ID":"middleware_id","Customer_ID":"customer_id","Server_ID":"server_id","Instance_Name":"middleware_name","Type (Tomcat/IIS/Apache HTTP/ActiveMQ/Oracle Application Server/Other)":"middleware_type","Version":"version","Port":"port","Environment (Prod/Test/Dev/Unknown)":"environment","Status (Running/Stopped/Unknown)":"status","Management_Scope":"management_scope","Source":"source","Last_Updated":"last_updated","Notes":"notes" },
  applications: { "Application_ID":"application_id","Customer_ID":"customer_id","Application_Name":"application_name","Application_Name_Other":"application_name_other","Version":"version","Environment (Prod/Test/Dev/Unknown)":"environment","Server_ID":"server_id","Middleware_ID":"middleware_id","Database_ID":"database_id","Management_Scope":"management_scope","Status (Active/Inactive/Unknown)":"status","Source":"source","Last_Updated":"last_updated","Notes":"notes" },
  engagements: {
    "customer_id":"customer_id","start_date":"start_date","end_date":"end_date",
    "contract_value":"contract_value","lead_consultant":"lead_consultant",
    "supporting_consultants":"supporting_consultants",
    "managed_services":"managed_services","banner_support":"banner_support",
    "colleague_support":"colleague_support","cloud_hosting":"cloud_hosting",
    "security_services":"security_services","monitoring_services":"monitoring_services",
    "backup_dr":"backup_dr","project_services":"project_services",
    "notes":"notes"
  }
};

// ============================================================
// AUDIT LOGGING
// ============================================================
async function logAudit(action, tableName, recordId, oldData, newData, userEmail) {
  try {
    await supabase.from("audit_log").insert({
      user_id: (await supabase.auth.getUser()).data.user?.id,
      user_email: userEmail,
      action, table_name: tableName, record_id: String(recordId),
      old_data: oldData || null, new_data: newData || null,
    });
  } catch (e) { console.warn("Audit log write failed:", e); }
}

// ============================================================
// FUZZY CUSTOMER MATCHING
// ============================================================
function normalizeCustomerName(name) {
  if (!name) return "";
  return name.toLowerCase()
    .replace(/(university|univ|college|coll|institute|inst|community|comm|school|district|dist)/g, m => ({
      university:"univ", college:"coll", institute:"inst", community:"comm", school:"sch", district:"dist"
    }[m] || m))
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function similarityScore(a, b) {
  const na = normalizeCustomerName(a);
  const nb = normalizeCustomerName(b);
  if (na === nb) return 1.0;
  if (na.includes(nb) || nb.includes(na)) return 0.9;
  // Token overlap score
  const ta = new Set(na.split(" "));
  const tb = new Set(nb.split(" "));
  const intersection = [...ta].filter(t => tb.has(t)).length;
  const union = new Set([...ta, ...tb]).size;
  return union === 0 ? 0 : intersection / union;
}

function matchCustomer(rawName, customers) {
  if (!rawName) return { type: "none", customer: null, score: 0 };
  const exact = customers.find(c =>
    c.customer_name?.toLowerCase().trim() === rawName.toLowerCase().trim()
  );
  if (exact) return { type: "exact", customer: exact, score: 1.0 };
  let best = null, bestScore = 0;
  for (const c of customers) {
    const score = similarityScore(rawName, c.customer_name);
    if (score > bestScore) { bestScore = score; best = c; }
  }
  if (bestScore >= 0.8) return { type: "fuzzy", customer: best, score: bestScore };
  return { type: "none", customer: null, score: bestScore };
}

// ============================================================
// ID GENERATION
// ============================================================
async function generateId(type) {
  const schema = SCHEMA[type];
  // Fetch the highest existing ID and increment — loop until we find one not in use
  // (guards against gaps or concurrent inserts producing the same value)
  const { data } = await supabase
    .from(schema.table)
    .select(schema.idField)
    .order(schema.idField, { ascending: false })
    .limit(1);
  const last  = data?.[0]?.[schema.idField] || "";
  const match = last.match(/\d+$/);
  let next    = match ? parseInt(match[0]) + 1 : 1;
  // Verify the candidate ID doesn't already exist (handles gaps/reuse edge cases)
  for (let attempt = 0; attempt < 10; attempt++) {
    const candidate = `${schema.idPrefix}-${String(next).padStart(5, "0")}`;
    const { data: existing } = await supabase
      .from(schema.table).select(schema.idField).eq(schema.idField, candidate).limit(1);
    if (!existing || existing.length === 0) return candidate;
    next++;
  }
  return `${schema.idPrefix}-${String(next).padStart(5, "0")}`;
}

// ============================================================
// CSV UTILITIES
// ============================================================
function parseCSV(text) {
  // RFC 4180-compliant parser — correctly handles quoted fields containing
  // commas, escaped double-quotes, and embedded newlines (e.g. CrowdStrike
  // policy exports where policy fields contain newlines inside quotes).
  if (!text || !text.trim()) return [];

  const records = [];
  let fields = [];
  let field = "";
  let inQuotes = false;
  let i = 0;
  const len = text.length;

  while (i < len) {
    const ch  = text[i];
    const nch = text[i + 1];

    if (inQuotes) {
      if (ch === '"' && nch === '"') {
        field += '"'; i += 2;           // escaped double-quote "" → "
      } else if (ch === '"') {
        inQuotes = false; i++;           // closing quote
      } else {
        field += ch; i++;               // any char incl. \r \n inside quotes
      }
    } else {
      if (ch === '"') {
        inQuotes = true; i++;
      } else if (ch === ',') {
        fields.push(field.trim()); field = ""; i++;
      } else if (ch === '\r' && nch === '\n') {
        fields.push(field.trim()); records.push(fields);
        fields = []; field = ""; i += 2;
      } else if (ch === '\n' || ch === '\r') {
        fields.push(field.trim()); records.push(fields);
        fields = []; field = ""; i++;
      } else {
        field += ch; i++;
      }
    }
  }
  // Flush last field / record
  if (field || fields.length) {
    fields.push(field.trim());
    if (fields.some(f => f)) records.push(fields);
  }

  if (!records.length) return [];

  const headers = records[0].map(h => h.replace(/^"+|"+$/g, "").trim());
  return records.slice(1)
    .filter(r => r.some(v => v))
    .map(r => {
      const obj = {};
      headers.forEach((h, idx) => {
        obj[h] = (r[idx] ?? "").replace(/^"+|"+$/g, "");
      });
      return obj;
    });
}

function toCSV(headers, rows) {
  const esc = v => { const s = String(v ?? ""); return (s.includes(",") || s.includes('"') || s.includes("\n")) ? `"${s.replace(/"/g, '""')}"` : s; };
  return [headers.map(esc).join(","), ...rows.map(r => headers.map(h => esc(r[h])).join(","))].join("\n");
}

function downloadCSV(filename, headers, rows) {
  const blob = new Blob([toCSV(headers, rows)], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function buildXLSXTemplate(XLSX, schema) {
  const today = new Date().toISOString().split("T")[0];
  const example = {};
  schema.fields.forEach(f => {
    if (f.readonly)               example[f.key] = `${schema.idPrefix}-00001`;
    else if (f.type === "asset-link") example[f.key] = "";
    else if (f.type === "select") example[f.key] = f.options[0];
    else if (f.type === "date")   example[f.key] = today;
    else                          example[f.key] = "";
  });

  const headers = schema.fields.map(f => f.key);

  // ── Tab 1: Data ───────────────────────────────────────────
  const dataRows = [headers, headers.map(h => example[h] ?? "")];
  const wsData   = XLSX.utils.aoa_to_sheet(dataRows);
  wsData["!cols"] = headers.map(() => ({ wch: 24 }));

  // ── Tab 2: Valid Values reference ─────────────────────────
  const refRows = [
    // ── How to use this template ──────────────────────────
    ["HOW TO USE THIS TEMPLATE", "", ""],
    ["", "", ""],
    ["Step 1", "Fill in your data on the 'Data' tab only.", "Row 1 is the header row — do not change column names."],
    ["", "", "Row 2 is an example row — delete it before uploading."],
    ["Step 2", "Delete this 'Valid Values' tab before saving as CSV.", "The upload system expects only the Data tab content."],
    ["Step 3", "Save / Export as CSV:", "Excel: File > Save As > CSV (Comma delimited) (*.csv)"],
    ["", "", "Google Sheets: File > Download > Comma Separated Values (.csv)"],
    ["Step 4", "Upload the .csv file on the Upload Data page.", "Use the drag-and-drop zone or click to browse."],
    ["", "", ""],
    ["IMPORTANT", "Do NOT include this Valid Values tab in the CSV.", "Only the Data tab columns should be present in the upload file."],
    ["", "", ""],
    // ── Field reference ───────────────────────────────────
    ["FIELD REFERENCE", "", ""],
    ["Field", "Valid Values", "Notes"],
  ];
  schema.fields.forEach(f => {
    if (f.type === "select") {
      refRows.push([f.key, f.options.join(", "), "Choose one of the listed values"]);
    } else if (f.type === "asset-link") {
      refRows.push([f.key, "(ID from " + f.assetType + " table)", "Optional — leave blank if unknown"]);
    } else if (f.readonly) {
      refRows.push([f.key, `${schema.idPrefix}-NNNNN`, "Auto-assigned — leave blank for new records"]);
    } else if (f.type === "date") {
      refRows.push([f.key, "YYYY-MM-DD", "e.g. " + today]);
    } else if (f.required) {
      refRows.push([f.key, "(free text)", "Required"]);
    } else {
      refRows.push([f.key, "(free text)", "Optional"]);
    }
  });
  const wsRef  = XLSX.utils.aoa_to_sheet(refRows);
  wsRef["!cols"] = [{ wch: 32 }, { wch: 64 }, { wch: 68 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsData, "Data");
  XLSX.utils.book_append_sheet(wb, wsRef,  "Valid Values");
  XLSX.writeFile(wb, `EMS_${schema.label}_Template.xlsx`);
}

function generateTemplate(type) {
  const schema = SCHEMA[type];

  // Helper that runs once SheetJS is confirmed available
  const run = (XLSX) => buildXLSXTemplate(XLSX, schema);

  // SheetJS already on window (loaded by UploadPage or index.html)
  if (window.XLSX) { run(window.XLSX); return; }

  // Dynamically load SheetJS from CDN then run
  const script = document.createElement("script");
  script.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
  script.onload  = () => run(window.XLSX);
  script.onerror = () => {
    // CDN unavailable — fall back to CSV
    const headers = schema.fields.map(f => f.key);
    const example = {};
    schema.fields.forEach(f => {
      if (f.readonly)               example[f.key] = `${schema.idPrefix}-00001`;
      else if (f.type === "select") example[f.key] = f.options[0];
      else if (f.type === "date")   example[f.key] = new Date().toISOString().split("T")[0];
      else                          example[f.key] = "";
    });
    downloadCSV(`EMS_${schema.label}_Template.csv`, headers, [example]);
  };
  document.head.appendChild(script);
}

function detectCSVType(headers) {
  for (const [type, mapping] of Object.entries(EXCEL_COLUMN_MAP)) {
    const excelKeys = Object.keys(mapping);
    const schemaKeys = Object.values(mapping);
    const matches = headers.filter(h => excelKeys.includes(h) || schemaKeys.includes(h));
    if (matches.length >= 2) return type;
  }
  return null;
}

function mapCSVRow(row, type) {
  const mapping = EXCEL_COLUMN_MAP[type];
  const schema = SCHEMA[type];
  const mapped = {};
  for (const [src, dst] of Object.entries(mapping)) {
    if (row[src] !== undefined) mapped[dst] = row[src];
  }
  schema.fields.forEach(f => {
    if (row[f.key] !== undefined && mapped[f.key] === undefined) mapped[f.key] = row[f.key];
  });
  return mapped;
}

// ============================================================
// STYLES
// ============================================================
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:ital,wght@0,300;0,400;0,500;1,300&family=Syne:wght@400;600;700;800&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #080b13; --bg2: #0e1120; --bg3: #141828; --border: #1a2035;
    --text: #e8eaf6; --text2: #6b7a99; --text3: #3d4a66;
    --accent: #6366f1; --accent2: #818cf8; --accent3: #4f46e5;
    --danger: #ef4444; --success: #10b981; --warning: #f59e0b; --info: #0ea5e9;
    --radius: 8px; --font-mono: 'DM Mono', monospace; --font: 'Syne', sans-serif;
  }
  body { background: var(--bg); color: var(--text); font-family: var(--font); min-height: 100vh; }
  
  /* AUTH */
  .auth-wrap { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: radial-gradient(ellipse 80% 60% at 20% 40%, #0d1040 0%, var(--bg) 65%); }
  .auth-card { background: var(--bg2); border: 1px solid var(--border); border-radius: 16px; padding: 2.5rem; width: 440px; max-width: 95vw; }
  .auth-logo { font-size: 2rem; font-weight: 800; letter-spacing: -2px; color: var(--accent2); }
  .auth-tagline { font-family: var(--font-mono); font-size: 0.72rem; color: var(--text2); margin-bottom: 0.25rem; }
  .auth-tabs { display: flex; border-bottom: 1px solid var(--border); margin: 1.5rem 0 1.5rem; }
  .auth-tab { padding: 0.6rem 1rem; font-size: 0.88rem; font-weight: 600; cursor: pointer; border-bottom: 2px solid transparent; color: var(--text2); transition: all 0.15s; }
  .auth-tab.active { color: var(--accent2); border-bottom-color: var(--accent); }
  .mfa-section { margin-top: 1.25rem; padding: 1rem; background: rgba(99,102,241,0.08); border: 1px solid rgba(99,102,241,0.2); border-radius: var(--radius); }
  .mfa-qr { display: flex; justify-content: center; margin: 1rem 0; }
  .mfa-qr img { border: 3px solid white; border-radius: 4px; }
  .pending-notice { padding: 1rem; background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.25); border-radius: var(--radius); font-family: var(--font-mono); font-size: 0.82rem; color: var(--warning); text-align: center; line-height: 1.6; }

  /* LAYOUT */
  .app { display: flex; height: 100vh; overflow: hidden; }
  .sidebar { width: 248px; background: var(--bg2); border-right: 1px solid var(--border); display: flex; flex-direction: column; flex-shrink: 0; height: 100vh; overflow-y: auto; }
  .sidebar-head { padding: 1.5rem 1.5rem 0; }
  .sidebar-logo { font-size: 1.5rem; font-weight: 800; letter-spacing: -1.5px; color: var(--accent2); }
  .sidebar-sub { font-family: var(--font-mono); font-size: 0.68rem; color: var(--text2); margin-bottom: 1.5rem; }
  .sidebar-user { padding: 0.75rem 1rem; margin: 0 0.75rem; background: var(--bg3); border-radius: var(--radius); border: 1px solid var(--border); margin-bottom: 1rem; }
  .sidebar-user-name { font-size: 0.85rem; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sidebar-user-role { font-family: var(--font-mono); font-size: 0.68rem; color: var(--text2); margin-top: 0.15rem; }
  .sidebar-section { font-family: var(--font-mono); font-size: 0.62rem; letter-spacing: 0.12em; text-transform: uppercase; color: var(--text3); padding: 0 1.5rem; margin: 1rem 0 0.4rem; }
  .sidebar-item { display: flex; align-items: center; gap: 0.65rem; padding: 0.55rem 1.5rem; cursor: pointer; border-left: 2px solid transparent; font-size: 0.88rem; font-weight: 600; color: var(--text2); transition: all 0.12s; }
  .sidebar-item:hover { color: var(--text); background: rgba(99,102,241,0.06); }
  .sidebar-item.active { color: var(--accent2); background: rgba(99,102,241,0.1); border-left-color: var(--accent); }
  .sidebar-count { margin-left: auto; font-family: var(--font-mono); font-size: 0.68rem; background: var(--bg); padding: 0.1rem 0.45rem; border-radius: 20px; color: var(--text3); }
  .sidebar-footer { margin-top: auto; padding: 1rem 1rem 1.5rem; display: flex; flex-direction: column; gap: 0.5rem; }
  .sidebar-lock-btn { width: 100%; padding: 0.5rem; background: transparent; border: 1px solid var(--border); border-radius: var(--radius); color: var(--text2); font-family: var(--font-mono); font-size: 0.78rem; cursor: pointer; transition: all 0.15s; }
  .sidebar-lock-btn:hover { border-color: var(--danger); color: var(--danger); }

  /* MAIN */
  .main { flex: 1; overflow-y: auto; display: flex; flex-direction: column; height: 100vh; }
  .page-header { padding: 1.75rem 2rem 1.25rem; border-bottom: 1px solid var(--border); display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; }
  .page-title { font-size: 1.7rem; font-weight: 800; letter-spacing: -1px; display: flex; align-items: center; gap: 0.5rem; }
  .page-actions { margin-left: auto; display: flex; gap: 0.6rem; flex-wrap: wrap; align-items: center; }
  .page-content { padding: 1.5rem 2rem; flex: 1; }

  /* BUTTONS */
  .btn { padding: 0.5rem 1.1rem; border-radius: var(--radius); border: none; font-family: var(--font); font-weight: 700; font-size: 0.84rem; cursor: pointer; transition: all 0.15s; display: inline-flex; align-items: center; gap: 0.45rem; white-space: nowrap; }
  .btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-primary { background: var(--accent); color: white; }
  .btn-primary:hover:not(:disabled) { background: var(--accent3); }
  .btn-secondary { background: var(--bg3); color: var(--text); border: 1px solid var(--border); }
  .btn-secondary:hover:not(:disabled) { border-color: var(--accent); color: var(--accent2); }
  .btn-danger { background: rgba(239,68,68,0.12); color: var(--danger); border: 1px solid rgba(239,68,68,0.25); }
  .btn-danger:hover:not(:disabled) { background: rgba(239,68,68,0.22); }
  .btn-success { background: rgba(16,185,129,0.12); color: var(--success); border: 1px solid rgba(16,185,129,0.25); }
  .btn-warning { background: rgba(245,158,11,0.12); color: var(--warning); border: 1px solid rgba(245,158,11,0.25); }
  .btn-sm { padding: 0.28rem 0.65rem; font-size: 0.76rem; }
  .btn-full { width: 100%; justify-content: center; padding: 0.75rem; }

  /* FORM ELEMENTS */
  .form-group { display: flex; flex-direction: column; gap: 0.35rem; }
  .form-group.full { grid-column: 1 / -1; }
  .form-label { font-family: var(--font-mono); font-size: 0.7rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text2); }
  .form-label .req { color: var(--danger); }
  .form-input, .form-select, .form-textarea { background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius); padding: 0.6rem 0.85rem; color: var(--text); font-family: var(--font-mono); font-size: 0.86rem; outline: none; transition: border-color 0.2s; width: 100%; }
  .form-input:focus, .form-select:focus, .form-textarea:focus { border-color: var(--accent); }
  .form-input[readonly] { opacity: 0.55; cursor: default; }
  .form-textarea { resize: vertical; min-height: 80px; }
  .form-select option { background: var(--bg2); }
  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .form-mb { margin-bottom: 1rem; }
  .input-help { font-family: var(--font-mono); font-size: 0.72rem; color: var(--text2); margin-top: 0.3rem; }

  /* TOOLBAR */
  .toolbar { display: flex; gap: 0.65rem; margin-bottom: 1.1rem; flex-wrap: wrap; align-items: center; }
  .search-input { flex: 1; min-width: 200px; background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius); padding: 0.5rem 0.9rem; color: var(--text); font-family: var(--font-mono); font-size: 0.86rem; outline: none; transition: border-color 0.2s; }
  .search-input:focus { border-color: var(--accent); }
  .record-count { font-family: var(--font-mono); font-size: 0.76rem; color: var(--text2); white-space: nowrap; }

  /* TABLE */
  .table-wrap { overflow-x: auto; border-radius: var(--radius); border: 1px solid var(--border); position: relative; }
  .table-scroll-hint { position: sticky; bottom: 0; left: 0; right: 0; height: 0; }
  table { width: 100%; border-collapse: collapse; font-size: 0.83rem; }
  thead th { background: var(--bg3); padding: 0.7rem 1rem; text-align: left; font-family: var(--font-mono); font-size: 0.68rem; letter-spacing: 0.09em; text-transform: uppercase; color: var(--text2); white-space: nowrap; border-bottom: 1px solid var(--border); cursor: pointer; user-select: none; }
  thead th:hover { color: var(--text); }
  tbody tr { border-bottom: 1px solid var(--border); transition: background 0.1s; }
  tbody tr:last-child { border-bottom: none; }
  tbody tr:hover { background: rgba(99,102,241,0.04); }
  tbody td { padding: 0.6rem 1rem; color: var(--text); font-family: var(--font-mono); font-size: 0.8rem; white-space: nowrap; max-width: 200px; overflow: hidden; text-overflow: ellipsis; }
  .td-actions { display: flex; gap: 0.35rem; }

  /* BADGES */
  .badge { display: inline-block; padding: 0.13rem 0.5rem; border-radius: 20px; font-family: var(--font-mono); font-size: 0.7rem; font-weight: 500; }
  .badge-green { background: rgba(16,185,129,0.13); color: var(--success); }
  .badge-red { background: rgba(239,68,68,0.13); color: var(--danger); }
  .badge-yellow { background: rgba(245,158,11,0.13); color: var(--warning); }
  .badge-gray { background: rgba(107,122,153,0.13); color: var(--text2); }
  .badge-blue { background: rgba(14,165,233,0.13); color: var(--info); }
  .badge-purple { background: rgba(99,102,241,0.13); color: var(--accent2); }

  /* MODAL */
  .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.8); display: flex; align-items: flex-start; justify-content: center; z-index: 1000; overflow-y: auto; padding: 2rem 1rem; }
  .modal { background: var(--bg2); border: 1px solid var(--border); border-radius: 12px; width: 720px; max-width: 100%; }
  .modal-header { padding: 1.5rem; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
  .modal-title { font-size: 1.15rem; font-weight: 700; }
  .modal-close { background: none; border: none; color: var(--text2); font-size: 1.5rem; cursor: pointer; line-height: 1; transition: color 0.15s; }
  .modal-close:hover { color: var(--text); }
  .modal-body { padding: 1.5rem; }
  .modal-footer { padding: 1rem 1.5rem; border-top: 1px solid var(--border); display: flex; gap: 0.65rem; justify-content: flex-end; }

  /* DASHBOARD */
  .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(175px, 1fr)); gap: 1rem; margin-bottom: 1.75rem; }
  .stat-card { background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius); padding: 1.25rem; position: relative; overflow: hidden; }
  .stat-icon { font-size: 1.5rem; margin-bottom: 0.5rem; }
  .stat-value { font-size: 2rem; font-weight: 800; letter-spacing: -2px; line-height: 1; }
  .stat-label { font-family: var(--font-mono); font-size: 0.7rem; color: var(--text2); margin-top: 0.25rem; text-transform: uppercase; letter-spacing: 0.08em; }
  .stat-sub { font-family: var(--font-mono); font-size: 0.7rem; color: var(--text3); margin-top: 0.1rem; }

  /* INFO BLOCKS */
  .info-block { padding: 1rem 1.25rem; border-radius: var(--radius); font-family: var(--font-mono); font-size: 0.82rem; line-height: 1.6; margin-bottom: 1.25rem; }
  .info-block.accent { background: rgba(99,102,241,0.08); border: 1px solid rgba(99,102,241,0.2); color: var(--text2); }
  .info-block.warning { background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.2); color: var(--warning); }
  .info-block.danger { background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2); color: var(--danger); }
  .info-block.success { background: rgba(16,185,129,0.08); border: 1px solid rgba(16,185,129,0.2); color: var(--success); }
  .info-block strong { color: var(--accent2); }

  /* UPLOAD */
  .upload-zone { border: 2px dashed var(--border); border-radius: var(--radius); padding: 2.5rem; text-align: center; cursor: pointer; transition: all 0.2s; }
  .upload-zone:hover, .upload-zone.drag-over { border-color: var(--accent); background: rgba(99,102,241,0.05); }
  .upload-icon { font-size: 2.5rem; margin-bottom: 0.75rem; }
  .upload-text { color: var(--text2); font-family: var(--font-mono); font-size: 0.83rem; line-height: 1.7; }
  .upload-text strong { color: var(--accent2); }
  .progress-bar { height: 4px; background: var(--bg3); border-radius: 2px; overflow: hidden; margin-top: 0.75rem; }
  .progress-fill { height: 100%; background: var(--accent); transition: width 0.3s; }

  /* TEMPLATE CARDS */
  .template-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 0.85rem; }
  .template-card { background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius); padding: 1.1rem; cursor: pointer; transition: all 0.15s; }
  .template-card:hover { border-color: var(--accent); background: rgba(99,102,241,0.05); }
  .template-card-icon { font-size: 1.75rem; margin-bottom: 0.5rem; }
  .template-card-title { font-weight: 700; font-size: 0.95rem; margin-bottom: 0.2rem; }
  .template-card-desc { font-family: var(--font-mono); font-size: 0.74rem; color: var(--text2); }

  /* USERS */
  .user-card { background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius); padding: 1rem 1.25rem; display: flex; align-items: center; gap: 1rem; margin-bottom: 0.65rem; }
  .user-avatar { width: 40px; height: 40px; border-radius: 50%; background: var(--bg3); border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 1rem; color: var(--accent2); flex-shrink: 0; }
  .user-info { flex: 1; min-width: 0; }
  .user-name { font-weight: 600; font-size: 0.92rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .user-email { font-family: var(--font-mono); font-size: 0.76rem; color: var(--text2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .user-actions { display: flex; gap: 0.4rem; flex-shrink: 0; }

  /* AUDIT */
  .audit-row { padding: 0.65rem 1rem; border-bottom: 1px solid var(--border); display: grid; grid-template-columns: 140px 80px 120px 1fr; gap: 1rem; font-family: var(--font-mono); font-size: 0.78rem; align-items: center; }
  .audit-row:hover { background: rgba(99,102,241,0.04); }

  /* EXPORT PANEL */
  .export-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 0.85rem; }
  .export-card { background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius); padding: 1.1rem; display: flex; flex-direction: column; gap: 0.6rem; }
  .export-card-title { font-weight: 700; font-size: 0.9rem; display: flex; align-items: center; gap: 0.5rem; }
  .export-card-desc { font-family: var(--font-mono); font-size: 0.74rem; color: var(--text2); flex: 1; }

  /* TOAST */
  .toast-container { position: fixed; bottom: 1.5rem; right: 1.5rem; z-index: 9999; display: flex; flex-direction: column; gap: 0.5rem; }
  .toast { padding: 0.7rem 1.1rem; border-radius: var(--radius); font-family: var(--font-mono); font-size: 0.82rem; animation: slideUp 0.25s ease; max-width: 340px; border: 1px solid; }
  .toast-success { background: rgba(16,185,129,0.15); border-color: rgba(16,185,129,0.4); color: var(--success); }
  .toast-error { background: rgba(239,68,68,0.15); border-color: rgba(239,68,68,0.4); color: var(--danger); }
  .toast-info { background: rgba(99,102,241,0.15); border-color: rgba(99,102,241,0.4); color: var(--accent2); }
  .toast-warning { background: rgba(245,158,11,0.15); border-color: rgba(245,158,11,0.4); color: var(--warning); }
  @keyframes slideUp { from { transform: translateY(12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

  /* MISC */
  .security-badge { display: inline-flex; align-items: center; gap: 0.4rem; font-family: var(--font-mono); font-size: 0.7rem; color: var(--success); background: rgba(16,185,129,0.1); border: 1px solid rgba(16,185,129,0.2); border-radius: 20px; padding: 0.2rem 0.65rem; }
  .role-badge-admin { display: inline-flex; align-items: center; gap: 0.3rem; font-family: var(--font-mono); font-size: 0.68rem; color: var(--accent2); background: rgba(99,102,241,0.12); border: 1px solid rgba(99,102,241,0.25); border-radius: 20px; padding: 0.18rem 0.55rem; }
  .role-badge-consultant { display: inline-flex; align-items: center; gap: 0.3rem; font-family: var(--font-mono); font-size: 0.68rem; color: var(--info); background: rgba(14,165,233,0.1); border: 1px solid rgba(14,165,233,0.2); border-radius: 20px; padding: 0.18rem 0.55rem; }
  .role-badge-manager { display: inline-flex; align-items: center; gap: 0.3rem; font-family: var(--font-mono); font-size: 0.68rem; color: #a78bfa; background: rgba(167,139,250,0.1); border: 1px solid rgba(167,139,250,0.25); border-radius: 20px; padding: 0.18rem 0.55rem; }
  .role-badge-pending { display: inline-flex; align-items: center; gap: 0.3rem; font-family: var(--font-mono); font-size: 0.68rem; color: var(--warning); background: rgba(245,158,11,0.1); border: 1px solid rgba(245,158,11,0.2); border-radius: 20px; padding: 0.18rem 0.55rem; }
  .divider { height: 1px; background: var(--border); margin: 1.25rem 0; }
  .empty-state { text-align: center; padding: 3rem; color: var(--text2); }
  .empty-icon { font-size: 2.5rem; margin-bottom: 0.75rem; }
  .empty-text { font-family: var(--font-mono); font-size: 0.85rem; }
  .loading-spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.2); border-top-color: white; border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .chart-bar-wrap { margin-bottom: 0.55rem; }
  .chart-bar-label { display: flex; justify-content: space-between; font-family: var(--font-mono); font-size: 0.76rem; color: var(--text2); margin-bottom: 0.2rem; }
  .chart-bar { height: 4px; background: var(--bg3); border-radius: 2px; }
  .chart-bar-fill { height: 100%; border-radius: 2px; transition: width 0.5s ease; }
  .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
  @media (max-width: 900px) { .two-col { grid-template-columns: 1fr; } .sidebar { display: none; } .form-grid { grid-template-columns: 1fr; } }
  .settings-section { background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius); padding: 1.5rem; margin-bottom: 1.25rem; }
  .settings-section-title { font-weight: 700; font-size: 1rem; margin-bottom: 1rem; display: flex; align-items: center; gap: 0.5rem; }
  .tab-bar { display: flex; border-bottom: 1px solid var(--border); margin-bottom: 1.5rem; gap: 0; }
  .tab-item { padding: 0.65rem 1.25rem; font-size: 0.85rem; font-weight: 600; cursor: pointer; border-bottom: 2px solid transparent; color: var(--text2); transition: all 0.15s; }
  .tab-item.active { color: var(--accent2); border-bottom-color: var(--accent); }
  .tab-item:hover { color: var(--text); }
`;

// ============================================================
// TOAST
// ============================================================
function useToast() {
  const [toasts, setToasts] = useState([]);
  const toast = useCallback((msg, type = "info") => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4500);
  }, []);
  return { toasts, toast };
}
function ToastContainer({ toasts }) {
  return <div className="toast-container">{toasts.map(t => <div key={t.id} className={`toast toast-${t.type}`}>{t.msg}</div>)}</div>;
}

// ============================================================
// ROLE BADGE
// ============================================================
function RoleBadge({ role }) {
  if (role === "administrator") return <span className="role-badge-admin">⬡ Administrator</span>;
  if (role === "manager") return <span className="role-badge-manager">◆ Manager</span>;
  if (role === "active_consultant") return <span className="role-badge-consultant">◈ Active Consultant</span>;
  return <span className="role-badge-pending">◎ Pending</span>;
}

function YNBadge({ val }) {
  if (!val) return <span className="badge badge-gray">—</span>;
  if (val === "Y" || val === "Yes") return <span className="badge badge-green">{val}</span>;
  if (val === "N" || val === "No") return <span className="badge badge-red">{val}</span>;
  return <span className="badge badge-yellow">{val}</span>;
}
function EnvBadge({ val }) {
  if (!val) return <span className="badge badge-gray">—</span>;
  const cls = { Prod:"badge-red", Test:"badge-yellow", Dev:"badge-blue", "Non-Prod":"badge-yellow" }[val] || "badge-gray";
  return <span className={`badge ${cls}`}>{val}</span>;
}

// ============================================================
// QR CODE RENDERER
// Uses the 'qrcode' npm package — fully offline, no external
// requests. Run: npm install qrcode
// Renders a proper PNG QR code that Google Authenticator,
// Authy, and 1Password can all scan reliably.
// ============================================================
function QRCodeCanvas({ value, size = 200 }) {
  const canvasRef = useRef(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!value || !canvasRef.current) return;
    setError(false);
    import("qrcode").then(QRCode => {
      QRCode.toCanvas(canvasRef.current, value, {
        width: size,
        margin: 2,
        color: { dark: "#000000", light: "#ffffff" },
      }, (err) => {
        if (err) {
          console.error("QR generation error:", err);
          setError(true);
        }
      });
    }).catch(err => {
      console.error("qrcode package not found — run: npm install qrcode", err);
      setError(true);
    });
  }, [value, size]);

  if (error) {
    return (
      <div style={{
        width: size, height: size, background: "white", borderRadius: 4,
        border: "3px solid white", display: "flex", alignItems: "center",
        justifyContent: "center", textAlign: "center", padding: "1rem",
        fontFamily: "monospace", fontSize: "0.78rem", color: "#444",
      }}>
        QR unavailable — use the manual key below
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{ border: "3px solid white", borderRadius: 4, display: "block" }}
    />
  );
}

// ============================================================
// AUTH SCREEN
// ============================================================
function AuthScreen({ toast, suppressAuthListener, onSignIn }) {
  const [tab, setTab] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSetup, setMfaSetup] = useState(null);   // { qr, secret, factorId }
  const [loading, setLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState(null);
  const [mfaChallengeId, setMfaChallengeId] = useState(null);
  const [verifyStep, setVerifyStep] = useState(false);  // after signin, before MFA verify
  const [showPassword, setShowPassword] = useState(false);

  const handleSignIn = async () => {
    if (!email || !password) { toast("Enter email and password", "error"); return; }
    // Always reset the auth listener at the start of a fresh sign-in attempt
    // so any suppressed state from a previous enrollment flow is cleared.
    suppressAuthListener.current = false;
    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { toast(error.message, "error"); setLoading(false); return; }

    // Always check what factors exist regardless of session state
    const { data: factorsData } = await supabase.auth.mfa.listFactors();
    const totp = factorsData?.totp?.[0];

    if (data.session === null) {
      // Supabase requires MFA verification before granting a session.
      // Two sub-cases:
      //   A) A verified factor exists — prompt for the code
      //   B) No verified factor exists — user deleted their factor and needs
      //      to re-enroll. Trigger the MFA enrollment flow.
      if (totp) {
        // Case A: verified factor present, challenge it
        const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId: totp.id });
        if (challengeError) { toast("MFA challenge failed", "error"); setLoading(false); return; }
        setMfaFactorId(totp.id);
        setMfaChallengeId(challengeData.id);
        setMfaRequired(true);
        setLoading(false);
        return;
      } else {
        // Case B: no factor — we need a session to enroll MFA.
        // Sign in via admin workaround: temporarily disable MFA requirement
        // by signing out and re-signing in with the service role is not
        // possible client-side. Instead, enroll a new factor now — Supabase
        // allows mfa.enroll() even without a fully-verified session.
        suppressAuthListener.current = true;
        const { data: mfaData, error: mfaError } = await supabase.auth.mfa.enroll({
          factorType: "totp", issuer: "EMS CMDB", friendlyName: `EMS-${email}`
        });
        if (mfaError) {
          suppressAuthListener.current = false;
          toast("Could not start MFA re-enrollment: " + mfaError.message, "error");
          setLoading(false);
          return;
        }
        setMfaSetup({ qr: mfaData.totp.qr_code, secret: mfaData.totp.secret, factorId: mfaData.id });
        setLoading(false);
        return;
      }
    }

    // Session granted — check for an existing verified factor to challenge
    if (totp && totp.status === "verified") {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId: totp.id });
      if (challengeError) { toast("MFA challenge failed", "error"); setLoading(false); return; }
      setMfaFactorId(totp.id);
      setMfaChallengeId(challengeData.id);
      setMfaRequired(true);
      setLoading(false);
      return;
    }

    // No MFA factor at all — go straight to enrollment
    if (!totp) {
      suppressAuthListener.current = true;
      const { data: mfaData, error: mfaError } = await supabase.auth.mfa.enroll({
        factorType: "totp", issuer: "EMS CMDB", friendlyName: `EMS-${email}`
      });
      if (mfaError) {
        suppressAuthListener.current = false;
        toast("MFA setup failed: " + mfaError.message, "error");
        setLoading(false);
        return;
      }
      setMfaSetup({ qr: mfaData.totp.qr_code, secret: mfaData.totp.secret, factorId: mfaData.id });
      setLoading(false);
      return;
    }

    // Check profile status — session granted and MFA not required
    const { data: profile } = await supabase.from("profiles").select("role,is_active").eq("id", data.user.id).single();
    if (!profile?.is_active) { await supabase.auth.signOut(); setPendingApproval(true); }
    setLoading(false);
  };

  const handleMFAVerify = async () => {
    if (!mfaCode || !mfaFactorId || !mfaChallengeId) return;
    setLoading(true);
    const { error } = await supabase.auth.mfa.verify({ factorId: mfaFactorId, challengeId: mfaChallengeId, code: mfaCode });
    if (error) {
      toast("Invalid MFA code — please try again", "error");
      setLoading(false);
      return;
    }
    // Verify succeeded — Supabase has upgraded the session.
    // Release the listener suppression, then get the current session
    // and call onSignIn directly so the App navigates immediately
    // without depending on onAuthStateChange firing reliably.
    suppressAuthListener.current = false;
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      toast("Signed in successfully", "success");
      onSignIn(session);
    } else {
      toast("Session error after MFA — please try signing in again", "error");
    }
    setLoading(false);
  };

  const handleSignUp = async () => {
    if (!email || !password || !fullName) { toast("All fields required", "error"); return; }
    if (password.length < 12) { toast("Password must be at least 12 characters", "error"); return; }
    setLoading(true);

    // Set BEFORE any Supabase call so the listener can never fire during this flow
    suppressAuthListener.current = true;

    const { data, error } = await supabase.auth.signUp({ email, password, options: { data: { full_name: fullName } } });
    if (error) {
      suppressAuthListener.current = false;
      toast(error.message, "error");
      setLoading(false);
      return;
    }

    // If session is null, email confirmation is still on in Supabase
    if (!data.session) {
      suppressAuthListener.current = false;
      toast("Email confirmation is still enabled — contact your system administrator to disable it, then delete this account and try again.", "error");
      await supabase.auth.signOut();
      setLoading(false);
      return;
    }

    // Verify we have a valid user ID before enrolling MFA
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user?.id) {
      suppressAuthListener.current = false;
      toast("Session error: " + (userError?.message || "no user returned"), "error");
      await supabase.auth.signOut();
      setLoading(false);
      return;
    }

    // Enroll MFA
    const { data: mfaData, error: mfaError } = await supabase.auth.mfa.enroll({ factorType: "totp", issuer: "EMS CMDB", friendlyName: `EMS-${email}` });
    if (mfaError) {
      suppressAuthListener.current = false;
      toast("MFA setup failed: " + mfaError.message, "error");
      await supabase.auth.signOut();
      setLoading(false);
      return;
    }

    // Show QR code — listener stays suppressed until handleMFAEnroll completes
    setMfaSetup({ qr: mfaData.totp.qr_code, secret: mfaData.totp.secret, factorId: mfaData.id });
    setLoading(false);
  };

  const handleMFAEnroll = async () => {
    if (!mfaCode || mfaCode.length !== 6 || !mfaSetup) return;
    setLoading(true);
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId: mfaSetup.factorId });
      if (challengeError) {
        toast("MFA challenge failed: " + challengeError.message, "error");
        setLoading(false);
        return;
      }
      const { error: verifyError } = await supabase.auth.mfa.verify({ factorId: mfaSetup.factorId, challengeId: challengeData.id, code: mfaCode });
      if (verifyError) {
        toast("Invalid code — check your authenticator app and try again", "error");
        setLoading(false);
        return;
      }
      // Sign out first, then release listener, then show pending screen
      await supabase.auth.signOut();
      suppressAuthListener.current = false;
      setMfaSetup(null);
      setMfaCode("");
      setPendingApproval(true);
    } catch (e) {
      suppressAuthListener.current = false;
      toast("Unexpected error: " + e.message, "error");
    }
    setLoading(false);
  };

  if (pendingApproval) return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-logo">EMS CMDB</div>
        <div className="auth-tagline">Configuration Management Database</div>
        <div className="divider" />
        <div className="pending-notice">
          ⏳ Account created & MFA configured.<br /><br />
          Your account is <strong>pending administrator approval</strong>.<br />
          You will be notified once access is granted.<br /><br />
          <span style={{ color: "var(--text2)" }}>Contact your EMS administrator if urgent.</span>
        </div>
        <div className="divider" />
        <button className="btn btn-secondary btn-full" onClick={() => setPendingApproval(false)}>← Back to Sign In</button>
      </div>
    </div>
  );

  if (mfaSetup) return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-logo">EMS CMDB</div>
        <div className="auth-tagline">Two-Factor Authentication Setup</div>
        <div className="divider" />
        <div className="mfa-section">
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.82rem", color: "var(--text2)", marginBottom: "0.75rem" }}>
            <strong style={{ color: "var(--accent2)" }}>Step 1:</strong> Scan this QR code with your authenticator app (Google Authenticator, Authy, or 1Password)
          </div>
          <div className="mfa-qr">
            <QRCodeCanvas
              value={`otpauth://totp/EMS%20CMDB:${encodeURIComponent(email)}?secret=${mfaSetup.secret}&issuer=EMS%20CMDB`}
              size={180}
            />
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.75rem", color: "var(--text2)", wordBreak: "break-all", marginBottom: "1rem", padding: "0.5rem", background: "var(--bg)", borderRadius: "4px" }}>
            Can't scan? Manual key: <strong style={{ color: "var(--accent2)" }}>{mfaSetup.secret}</strong>
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.82rem", color: "var(--text2)", marginBottom: "0.75rem" }}>
            <strong style={{ color: "var(--accent2)" }}>Step 2:</strong> Enter the 6-digit code from your app to confirm setup
          </div>
          <input
            className="form-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="000000"
            value={mfaCode}
            onChange={e => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={e => e.key === "Enter" && mfaCode.length === 6 && handleMFAEnroll()}
            style={{ letterSpacing: "0.4em", textAlign: "center", fontSize: "1.2rem" }}
            autoFocus
          />
          <button
            className="btn btn-primary btn-full"
            style={{ marginTop: "1rem" }}
            onClick={handleMFAEnroll}
            disabled={loading || mfaCode.length !== 6}
          >
            {loading ? <span className="loading-spinner" /> : "Verify & Complete Registration"}
          </button>
          <div style={{ marginTop: "0.85rem", fontFamily: "var(--font-mono)", fontSize: "0.72rem", color: "var(--text3)", textAlign: "center" }}>
            Keep this screen open until you have scanned the QR code and verified your code successfully.
          </div>
        </div>
      </div>
    </div>
  );

  if (mfaRequired) return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-logo">EMS CMDB</div>
        <div className="auth-tagline">Two-Factor Authentication Required</div>
        <div className="divider" />
        <div className="mfa-section">
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.82rem", color: "var(--text2)", marginBottom: "1rem" }}>
            Enter the 6-digit code from your authenticator app
          </div>
          <input className="form-input" type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={mfaCode} onChange={e => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))} style={{ letterSpacing: "0.4em", textAlign: "center", fontSize: "1.2rem" }} autoFocus />
          <button className="btn btn-primary btn-full" style={{ marginTop: "1rem" }} onClick={handleMFAVerify} disabled={loading || mfaCode.length !== 6}>
            {loading ? <span className="loading-spinner" /> : "Verify"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-logo">EMS CMDB</div>
        <div className="auth-tagline" style={{ marginBottom: "1.5rem" }}>Configuration Management Database</div>
        <div className="auth-tabs">
          <div className={`auth-tab${tab === "signin" ? " active" : ""}`} onClick={() => setTab("signin")}>Sign In</div>
          <div className={`auth-tab${tab === "signup" ? " active" : ""}`} onClick={() => setTab("signup")}>Request Access</div>
        </div>
        {tab === "signin" ? (
          <>
            <div className="form-group form-mb">
              <label className="form-label">Email Address</label>
              <input className="form-input" type="email" placeholder="you@organization.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSignIn()} />
            </div>
            <div className="form-group form-mb">
              <label className="form-label">Password</label>
              <div style={{ position: "relative" }}>
                <input className="form-input" type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSignIn()} style={{ paddingRight: "2.75rem" }} />
                <button type="button" onClick={() => setShowPassword(s => !s)} style={{ position: "absolute", right: "0.6rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text2)", fontSize: "1rem", padding: "0.2rem", lineHeight: 1 }} title={showPassword ? "Hide password" : "Show password"}>
                  {showPassword ? "🙈" : "👁"}
                </button>
              </div>
            </div>
            <button className="btn btn-primary btn-full" onClick={handleSignIn} disabled={loading}>
              {loading ? <span className="loading-spinner" /> : "Sign In"}
            </button>
          </>
        ) : (
          <>
            <div className="info-block accent" style={{ marginBottom: "1.25rem" }}>
              <strong>MFA Required:</strong> All accounts require two-factor authentication. You will set this up during registration. Your account will need administrator approval before you can access data.
            </div>
            <div className="form-group form-mb">
              <label className="form-label">Full Name</label>
              <input className="form-input" type="text" value={fullName} onChange={e => setFullName(e.target.value)} />
            </div>
            <div className="form-group form-mb">
              <label className="form-label">Work Email</label>
              <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div className="form-group form-mb">
              <label className="form-label">Password (min 12 characters)</label>
              <div style={{ position: "relative" }}>
                <input className="form-input" type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} style={{ paddingRight: "2.75rem" }} />
                <button type="button" onClick={() => setShowPassword(s => !s)} style={{ position: "absolute", right: "0.6rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text2)", fontSize: "1rem", padding: "0.2rem", lineHeight: 1 }} title={showPassword ? "Hide password" : "Show password"}>
                  {showPassword ? "🙈" : "👁"}
                </button>
              </div>
            </div>
            <button className="btn btn-primary btn-full" onClick={handleSignUp} disabled={loading}>
              {loading ? <span className="loading-spinner" /> : "Register & Set Up MFA →"}
            </button>
          </>
        )}
        <div style={{ textAlign: "center", marginTop: "1.25rem" }}>
          <span className="security-badge">🔒 HTTPS · MFA Required · TLS 1.3 · Encrypted at Rest</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// RECORD MODAL
// ============================================================
// Fields that hold a customer_id and should show a customer name picker
const CUSTOMER_ID_FIELDS = new Set(["customer_id"]);
// Asset-link fields that show a customer-scoped dropdown to another asset table
const ASSET_LINK_FIELDS  = new Set(["server_id","middleware_id","database_id"]);
// Asset types that have a customer_id field and need the picker
const ASSETS_WITH_CUSTOMER = new Set(["servers","databases","endpoints","monitoring","network","middleware","applications"]);

function AssetLinkPicker({ label, fieldKey, assetType, displayField, customerId, value, onChange }) {
  const [open,    setOpen]    = useState(false);
  const [search,  setSearch]  = useState("");
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (!customerId) { setOptions([]); return; }
    const s = SCHEMA[assetType];
    supabase.from(s.table)
      .select(`${s.idField},${displayField},customer_id`)
      .eq("customer_id", customerId)
      .order(displayField)
      .then(({ data }) => setOptions(data || []));
  }, [customerId, assetType, displayField]);

  const s        = SCHEMA[assetType];
  const idField  = s.idField;
  const selected = options.find(o => o[idField] === value);
  const filtered = search ? options.filter(o => (o[displayField]||"").toLowerCase().includes(search.toLowerCase())) : options;

  return (
    <div style={{ position: "relative" }}>
      <div className="form-input"
        style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", userSelect: "none" }}
        onClick={() => setOpen(o => !o)}>
        <span style={{ color: selected ? "var(--text)" : "var(--text3)" }}>
          {!customerId ? "— Select a customer first —" : selected ? selected[displayField] : `— Select ${label} — (optional)`}
        </span>
        <span style={{ fontSize: "0.7rem", color: "var(--text3)" }}>▼</span>
      </div>
      {value && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.25rem" }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", color: "var(--text3)" }}>{value}</span>
          <button type="button" onClick={() => onChange("")}
            style={{ background: "none", border: "none", color: "var(--text3)", cursor: "pointer", fontSize: "0.75rem", padding: 0 }}>✕ clear</button>
        </div>
      )}
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200, background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", boxShadow: "0 4px 16px rgba(0,0,0,0.4)", maxHeight: 220, display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "0.5rem" }}>
            <input className="form-input" placeholder={`Search ${label.toLowerCase()}...`} value={search} autoFocus
              onChange={e => setSearch(e.target.value)} onClick={e => e.stopPropagation()} style={{ marginBottom: 0 }} />
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {!customerId
              ? <div style={{ padding: "0.75rem 1rem", color: "var(--text3)", fontSize: "0.82rem" }}>Select a customer first</div>
              : filtered.length === 0
              ? <div style={{ padding: "0.75rem 1rem", color: "var(--text3)", fontSize: "0.82rem" }}>No {label.toLowerCase()} found for this customer</div>
              : filtered.map(o => (
                <div key={o[idField]}
                  style={{ padding: "0.55rem 1rem", cursor: "pointer", fontSize: "0.84rem",
                    background: o[idField] === value ? "var(--bg3)" : "transparent", color: "var(--text)" }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--bg3)"}
                  onMouseLeave={e => e.currentTarget.style.background = o[idField] === value ? "var(--bg3)" : "transparent"}
                  onClick={() => { onChange(o[idField]); setOpen(false); setSearch(""); }}>
                  <div>{o[displayField]}</div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", color: "var(--text3)" }}>{o[idField]}</div>
                </div>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

function RecordModal({ type, record, onSave, onClose, prefill }) {
  const schema = SCHEMA[type];
  const isEdit = !!record;
  const [form, setForm] = useState(() => {
    if (record) return { ...record };
    // Build field-by-field so prefill values are never silently dropped.
    // Explicit non-empty prefill value always wins over blank default.
    const base = {};
    schema.fields.forEach(f => {
      if (prefill && prefill[f.key] !== undefined && prefill[f.key] !== "") {
        base[f.key] = prefill[f.key];
      } else {
        base[f.key] = "";
      }
    });
    return base;
  });
  const [loading, setLoading]       = useState(false);
  const [customers, setCustomers]   = useState([]);  // [{customer_id, customer_name}]
  const [custSearch, setCustSearch] = useState("");
  const [custOpen, setCustOpen]     = useState(false);
  const needsCustPicker = ASSETS_WITH_CUSTOMER.has(type);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!needsCustPicker) return;
    supabase.from("customers")
      .select("customer_id,customer_name")
      .eq("active","Yes")
      .order("customer_name")
      .then(({ data }) => setCustomers(data || []));
  }, [needsCustPicker]);

  // Derive display name for current customer_id value
  const selectedCustomer = customers.find(c => c.customer_id === form.customer_id);
  const filteredCusts = custSearch
    ? customers.filter(c => c.customer_name.toLowerCase().includes(custSearch.toLowerCase()))
    : customers;

  const handleSave = async () => {
    setLoading(true);
    await onSave(form);
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isEdit ? "Edit" : "Add"} {schema.label.slice(0, -1)}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            {schema.fields.map(f => (
              <div key={f.key} className={`form-group${f.type === "textarea" ? " full" : ""}`}>
                <label className="form-label">{f.label}{f.required && <span className="req"> *</span>}</label>

                {/* Customer ID field — replaced with searchable name picker */}
                {CUSTOMER_ID_FIELDS.has(f.key) && needsCustPicker ? (
                  <div style={{ position: "relative" }}>
                    <div
                      className="form-input"
                      style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", userSelect: "none" }}
                      onClick={() => setCustOpen(o => !o)}>
                      <span style={{ color: selectedCustomer ? "var(--text)" : "var(--text3)" }}>
                        {selectedCustomer ? selectedCustomer.customer_name : "— Select Customer —"}
                      </span>
                      <span style={{ fontSize: "0.7rem", color: "var(--text3)" }}>▼</span>
                    </div>
                    {form.customer_id && (
                      <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", color: "var(--text3)", marginTop: "0.25rem" }}>
                        ID: {form.customer_id}
                      </div>
                    )}
                    {custOpen && (
                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200, background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", boxShadow: "0 4px 16px rgba(0,0,0,0.4)", maxHeight: 240, display: "flex", flexDirection: "column" }}>
                        <div style={{ padding: "0.5rem" }}>
                          <input
                            className="form-input"
                            placeholder="Search customers..."
                            value={custSearch}
                            autoFocus
                            onChange={e => setCustSearch(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            style={{ marginBottom: 0 }} />
                        </div>
                        <div style={{ overflowY: "auto", flex: 1 }}>
                          {filteredCusts.length === 0
                            ? <div style={{ padding: "0.75rem 1rem", color: "var(--text3)", fontSize: "0.82rem" }}>No customers found</div>
                            : filteredCusts.map(c => (
                              <div key={c.customer_id}
                                style={{ padding: "0.55rem 1rem", cursor: "pointer", fontSize: "0.84rem",
                                  background: c.customer_id === form.customer_id ? "var(--bg3)" : "transparent",
                                  color: "var(--text)" }}
                                onMouseEnter={e => e.currentTarget.style.background = "var(--bg3)"}
                                onMouseLeave={e => e.currentTarget.style.background = c.customer_id === form.customer_id ? "var(--bg3)" : "transparent"}
                                onClick={() => { set("customer_id", c.customer_id); setCustOpen(false); setCustSearch(""); }}>
                                <div>{c.customer_name}</div>
                                <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", color: "var(--text3)" }}>{c.customer_id}</div>
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    )}
                  </div>
                ) : f.type === "asset-link" ? (
                  <AssetLinkPicker
                    label={f.label}
                    fieldKey={f.key}
                    assetType={f.assetType}
                    displayField={f.displayField}
                    customerId={form.customer_id}
                    value={form[f.key] || ""}
                    onChange={v => set(f.key, v)}
                  />
                ) : f.key === "application_name_other" ? (
                  // Only show when application_name === "Other"
                  form.application_name === "Other"
                    ? <input className="form-input" type="text" value={form[f.key] || ""} onChange={e => set(f.key, e.target.value)} placeholder="Specify application name..." />
                    : <span style={{ color: "var(--text3)", fontFamily: "var(--font-mono)", fontSize: "0.78rem" }}>— set Application to Other to enable —</span>
                ) : f.type === "select" ? (
                  <select className="form-select" value={form[f.key] || ""} onChange={e => set(f.key, e.target.value)}>
                    <option value="">— Select —</option>
                    {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === "textarea" ? (
                  <textarea className="form-textarea" value={form[f.key] || ""} onChange={e => set(f.key, e.target.value)} />
                ) : (
                  <input className="form-input" type={f.type} value={form[f.key] || ""} readOnly={f.readonly} onChange={e => set(f.key, e.target.value)} />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? <span className="loading-spinner" /> : isEdit ? "Save Changes" : "Add Record"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// TABLE VIEW
// ============================================================
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

function TableView({ type, records, customerMap, onEdit, onDelete, onActivate, onBulkActivate, onPromote, onBulkPromote, isAdmin, canEdit, loading }) {
  const schema = SCHEMA[type];
  const [search,     setSearch]     = useState("");
  const [custFilter, setCustFilter] = useState("");
  const [sortCol,    setSortCol]    = useState(schema.idField);
  const [sortDir,    setSortDir]    = useState(1);
  const [page,       setPage]       = useState(1);
  const [pageSize,   setPageSize]   = useState(100);
  const [selected,         setSelected]         = useState(new Set()); // for bulk activate
  const [selectedPromote,  setSelectedPromote]  = useState(new Set()); // for bulk promote

  const hasCustomer  = schema.fields.some(f => f.key === "customer_id");
  const displayFields = schema.fields.filter(f => f.type !== "textarea").slice(0, 8);
  const canPromote   = (type === "endpoints" || type === "monitoring") && !!onPromote;
  const canBulkProm  = (type === "endpoints" || type === "monitoring") && !!onBulkPromote;

  // Checkbox column shown when staged records exist (activate) OR bulk promote available
  const hasStagedRecords  = records.some(r => r.import_status === "staged");
  const showCheckboxCol   = hasStagedRecords || canBulkProm;

  const customerOptions = useMemo(() => {
    if (!hasCustomer || !customerMap) return [];
    const names = [...new Set(
      records.map(r => customerMap[r.customer_id]).filter(Boolean)
    )].sort();
    return names;
  }, [records, customerMap, hasCustomer]);

  useEffect(() => { setPage(1); }, [search, custFilter, pageSize]);
  useEffect(() => { setSelected(new Set()); setSelectedPromote(new Set()); }, [records]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return records
      .filter(r => {
        if (q && !Object.values(r).some(v => v && String(v).toLowerCase().includes(q))) return false;
        if (custFilter) {
          const name = customerMap?.[r.customer_id] || "";
          if (name !== custFilter) return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (sortCol === "customer_id" && customerMap) {
          const na = customerMap[a.customer_id] || a.customer_id || "";
          const nb = customerMap[b.customer_id] || b.customer_id || "";
          return na.localeCompare(nb) * sortDir;
        }
        return String(a[sortCol] || "").localeCompare(String(b[sortCol] || "")) * sortDir;
      });
  }, [records, search, custFilter, sortCol, sortDir, customerMap]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const pageRows   = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  // ── Activate selection helpers ──────────────────────────────────────────────
  const stagedOnPage         = pageRows.filter(r => r.import_status === "staged");
  const allPageStagedSelected = stagedOnPage.length > 0 && stagedOnPage.every(r => selected.has(r.id));

  const toggleRow = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAllPageStaged = () => {
    if (allPageStagedSelected) {
      setSelected(prev => { const next = new Set(prev); stagedOnPage.forEach(r => next.delete(r.id)); return next; });
    } else {
      setSelected(prev => { const next = new Set(prev); stagedOnPage.forEach(r => next.add(r.id)); return next; });
    }
  };

  // ── Promote selection helpers ───────────────────────────────────────────────
  const allPagePromoteSelected = pageRows.length > 0 && pageRows.every(r => selectedPromote.has(r.id));

  const togglePromoteRow = (id) => setSelectedPromote(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAllPagePromote = () => {
    if (allPagePromoteSelected) {
      setSelectedPromote(prev => { const next = new Set(prev); pageRows.forEach(r => next.delete(r.id)); return next; });
    } else {
      setSelectedPromote(prev => { const next = new Set(prev); pageRows.forEach(r => next.add(r.id)); return next; });
    }
  };

  const handleSort = col => {
    if (sortCol === col) setSortDir(d => -d);
    else { setSortCol(col); setSortDir(1); }
    setPage(1);
  };

  if (loading) return <div className="empty-state"><div className="loading-spinner" style={{ width: 32, height: 32, borderWidth: 3 }} /></div>;
  if (!records.length) return <div className="empty-state"><div className="empty-icon">{schema.icon}</div><div className="empty-text">No {schema.label.toLowerCase()} yet.<br />Add a record manually or upload a CSV.</div></div>;

  const PaginationBar = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.6rem 0.25rem", fontFamily: "var(--font-mono)", fontSize: "0.76rem", color: "var(--text2)", flexWrap: "wrap", gap: "0.5rem" }}>
      <span>
        Showing {filtered.length === 0 ? 0 : (safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)} of <strong>{filtered.length}</strong>
        {filtered.length !== records.length && <span style={{ color: "var(--text3)" }}> (filtered from {records.length})</span>}
      </span>
      <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
        <span style={{ color: "var(--text3)", marginRight: "0.25rem" }}>Per page:</span>
        <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
          style={{ fontFamily: "var(--font-mono)", fontSize: "0.74rem", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", color: "var(--text)", padding: "0.15rem 0.4rem" }}>
          {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <button className="btn btn-secondary btn-sm" onClick={() => setPage(1)} disabled={safePage === 1}>«</button>
        <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}>‹</button>
        <span style={{ padding: "0 0.35rem", whiteSpace: "nowrap" }}>Page {safePage} of {totalPages}</span>
        <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>›</button>
        <button className="btn btn-secondary btn-sm" onClick={() => setPage(totalPages)} disabled={safePage === totalPages}>»</button>
      </div>
    </div>
  );

  // Two separate checkbox columns: left = activate (staged only), right = promote
  // We merge them into one "select" column to keep the table clean.
  // When both are relevant, we show two checkbox columns with clear headers.
  const showActivateCol = hasStagedRecords;
  const showPromoteCol  = canBulkProm;

  return (
    <>
      {/* Search + Customer filter toolbar */}
      <div className="toolbar" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
        <input className="search-input" placeholder={`Search ${schema.label.toLowerCase()}...`}
          value={search} onChange={e => setSearch(e.target.value)} />
        {hasCustomer && customerOptions.length > 0 && (
          <select value={custFilter} onChange={e => setCustFilter(e.target.value)}
            style={{ fontFamily: "var(--font-mono)", fontSize: "0.78rem", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", color: custFilter ? "var(--text)" : "var(--text3)", padding: "0.4rem 0.75rem", minWidth: 180 }}>
            <option value="">All Customers</option>
            {customerOptions.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        )}
      </div>

      {/* Action bar — appears when any rows are selected for either action */}
      {(selected.size > 0 || selectedPromote.size > 0) && (
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "0.6rem 1rem", background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.25)", borderRadius: "var(--radius)", marginBottom: "0.5rem", flexWrap: "wrap" }}>
          {selected.size > 0 && (
            <>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.82rem", color: "var(--warning)" }}>
                ⏳ <strong>{selected.size}</strong> to activate
              </span>
              <button className="btn btn-success btn-sm" onClick={() => onBulkActivate && onBulkActivate([...selected])}>
                ✓ Activate ({selected.size})
              </button>
            </>
          )}
          {selected.size > 0 && selectedPromote.size > 0 && (
            <span style={{ color: "var(--border)", fontFamily: "var(--font-mono)" }}>|</span>
          )}
          {selectedPromote.size > 0 && (
            <>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.82rem", color: "var(--accent2)" }}>
                🖥️ <strong>{selectedPromote.size}</strong> to promote to Servers
              </span>
              <button className="btn btn-primary btn-sm" onClick={() => onBulkPromote && onBulkPromote([...selectedPromote])}>
                → Promote to Servers ({selectedPromote.size})
              </button>
            </>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => { setSelected(new Set()); setSelectedPromote(new Set()); }}>
            Clear All
          </button>
        </div>
      )}

      <PaginationBar />

      <div style={{ overflowX: "auto", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
        <table>
          <thead><tr>
            {showActivateCol && (
              <th style={{ width: 36, padding: "0.5rem", textAlign: "center", whiteSpace: "nowrap" }}
                title="Select staged rows to activate">
                <div style={{ fontSize: "0.62rem", color: "var(--text3)", marginBottom: "0.2rem" }}>Act.</div>
                <input type="checkbox" checked={allPageStagedSelected}
                  onChange={toggleAllPageStaged} style={{ cursor: "pointer" }}
                  disabled={stagedOnPage.length === 0} />
              </th>
            )}
            {showPromoteCol && (
              <th style={{ width: 36, padding: "0.5rem", textAlign: "center", whiteSpace: "nowrap" }}
                title="Select rows to promote to Servers">
                <div style={{ fontSize: "0.62rem", color: "var(--text3)", marginBottom: "0.2rem" }}>Srv.</div>
                <input type="checkbox" checked={allPagePromoteSelected}
                  onChange={toggleAllPagePromote} style={{ cursor: "pointer" }} />
              </th>
            )}
            {displayFields.map(f => (
              <th key={f.key} onClick={() => handleSort(f.key)} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                {f.key === "customer_id" && type !== "customers" ? "Customer" : f.label}
                {sortCol === f.key ? (sortDir > 0 ? " ↑" : " ↓") : ""}
              </th>
            ))}
            <th style={{ whiteSpace: "nowrap", minWidth: 100 }}>Actions</th>
          </tr></thead>
          <tbody>
            {pageRows.length === 0
              ? <tr><td colSpan={displayFields.length + (showActivateCol ? 1 : 0) + (showPromoteCol ? 1 : 0) + 1}
                  style={{ textAlign: "center", color: "var(--text3)", padding: "2rem" }}>
                  No records match your search or filter.
                </td></tr>
              : pageRows.map((r, i) => {
                const isStaged   = r.import_status === "staged";
                const isActSel   = selected.has(r.id);
                const isPromSel  = selectedPromote.has(r.id);
                const rowBg = isPromSel
                  ? "rgba(99,102,241,0.08)"
                  : isActSel ? "rgba(245,158,11,0.1)"
                  : isStaged ? "rgba(245,158,11,0.04)"
                  : "transparent";
                return (
                  <tr key={i} style={{ opacity: isStaged ? 0.85 : 1, background: rowBg }}>
                    {showActivateCol && (
                      <td style={{ textAlign: "center", padding: "0.4rem 0.5rem" }}>
                        {isStaged && (
                          <input type="checkbox" checked={isActSel}
                            onChange={() => toggleRow(r.id)} style={{ cursor: "pointer" }} />
                        )}
                      </td>
                    )}
                    {showPromoteCol && (
                      <td style={{ textAlign: "center", padding: "0.4rem 0.5rem" }}>
                        <input type="checkbox" checked={isPromSel}
                          onChange={() => togglePromoteRow(r.id)} style={{ cursor: "pointer" }} />
                      </td>
                    )}
                    {displayFields.map(f => (
                      <td key={f.key} title={f.key === "customer_id" ? (customerMap?.[r[f.key]] || r[f.key] || "") : (r[f.key] || "")}>
                        {f.key === "customer_id" && type !== "customers"
                          ? (customerMap?.[r.customer_id]
                              ? <span>{customerMap[r.customer_id]}</span>
                              : <span style={{ color: "var(--text3)", fontStyle: "italic" }}>{r.customer_id || "—"}</span>)
                          : f.type === "asset-link"
                            ? <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.78rem", color: "var(--text3)" }}>{r[f.key] || "—"}</span>
                          : f.type === "select" && (f.options?.includes("Y") || f.options?.includes("Yes"))
                            ? <YNBadge val={r[f.key]} />
                            : f.key === "environment" ? <EnvBadge val={r[f.key]} />
                            : (r[f.key] || <span style={{ color: "var(--text3)" }}>—</span>)
                        }
                      </td>
                    ))}
                    <td style={{ whiteSpace: "nowrap" }}>
                      <div className="td-actions">
                        {isStaged && <span className="badge badge-yellow">⏳</span>}
                        {canEdit && <button className="btn btn-secondary btn-sm" onClick={() => onEdit(r)}>Edit</button>}
                        {canEdit && isStaged && (
                          <button className="btn btn-success btn-sm" title="Activate this record"
                            onClick={() => onActivate && onActivate(r)}>✓</button>
                        )}
                        {canPromote && (
                          <button className="btn btn-secondary btn-sm" title="Add to Servers"
                            onClick={() => onPromote(r)}>→ Srv</button>
                        )}
                        {isAdmin && <button className="btn btn-danger btn-sm" onClick={() => onDelete(r)}>Del</button>}
                      </div>
                    </td>
                  </tr>
                );
              })
            }
          </tbody>
        </table>
      </div>
    </>
  );
}

// ============================================================
// ASSET PAGE
// ============================================================
function AssetPage({ type, profile, userEmail, toast, canManage }) {
  const schema = SCHEMA[type];
  const isAdmin = profile?.role === "administrator";
  // All active users can edit records in the console.
  // CSV bulk updates are still restricted to managers and admins only.
  const canEdit = true;
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [promotePrefill, setPromotePrefill] = useState(null); // non-null = promote modal open

  const handlePromoteSave = async (form) => {
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const payload = { ...form, updated_by: userId };
    payload["server_id"] = await generateId("servers");
    payload.created_by = userId;
    delete payload.id; delete payload.created_at; delete payload.updated_at;
    const { data, error } = await supabase.from("servers").insert(payload).select().single();
    if (error) { toast("Save failed: " + error.message, "error"); return; }
    await logAudit("INSERT", "servers", data.server_id, null, data, userEmail);
    toast("Server record created ✓", "success");
    setPromotePrefill(null);
  };

  const [customerMap, setCustomerMap] = useState({}); // customer_id -> customer_name

  useEffect(() => {
    supabase.from("customers").select("customer_id,customer_name")
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(c => { map[c.customer_id] = c.customer_name; });
        setCustomerMap(map);
      });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    // Fetch all rows — Supabase default caps at 1000, so we page through
    let allData = [], from = 0, batchSize = 1000, fetchError = null;
    while (true) {
      const { data: batch, error: bErr } = await supabase
        .from(schema.table).select("*").order(schema.idField)
        .range(from, from + batchSize - 1);
      if (bErr) { fetchError = bErr; break; }
      allData = allData.concat(batch || []);
      if (!batch || batch.length < batchSize) break;
      from += batchSize;
    }
    const data = allData, error = fetchError;
    if (error) { toast("Load failed: " + error.message, "error"); }
    else setRecords(data || []);
    setLoading(false);
  }, [schema.table, schema.idField, toast]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    const isEdit = !!form.id;
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const payload = { ...form, updated_by: userId };
    if (!isEdit) { payload[schema.idField] = await generateId(type); payload.created_by = userId; }
    delete payload.id; delete payload.created_at; delete payload.updated_at;
    const { data, error } = isEdit
      ? await supabase.from(schema.table).update(payload).eq("id", form.id).select().single()
      : await supabase.from(schema.table).insert(payload).select().single();
    if (error) { toast("Save failed: " + error.message, "error"); return; }
    await logAudit(isEdit ? "UPDATE" : "INSERT", schema.table, data[schema.idField], isEdit ? form : null, data, userEmail);
    toast(isEdit ? "Record updated" : "Record added", "success");
    setModal(null);
    load();
  };

  const handleActivate = async (record) => {
    const { error } = await supabase.from(schema.table)
      .update({ import_status: "active" })
      .eq("id", record.id);
    if (error) { toast("Activation failed: " + error.message, "error"); return; }
    await logAudit("ACTIVATE", schema.table, record[schema.idField], { import_status: "staged" }, { import_status: "active" }, userEmail);
    toast("Record activated ✓", "success");
    load();
  };

  const handleBulkActivate = async (ids) => {
    if (!ids || ids.length === 0) return;
    let succeeded = 0;
    for (const id of ids) {
      const record = records.find(r => r.id === id);
      if (!record || record.import_status !== "staged") continue;
      const { error } = await supabase.from(schema.table)
        .update({ import_status: "active" })
        .eq("id", id);
      if (error) { toast(`Activation failed for one record: ${error.message}`, "error"); continue; }
      await logAudit("ACTIVATE", schema.table, record[schema.idField], { import_status: "staged" }, { import_status: "active" }, userEmail);
      succeeded++;
    }
    if (succeeded > 0) {
      toast(`✓ Activated ${succeeded} record${succeeded !== 1 ? "s" : ""}`, "success");
      load();
    }
  };

  // Promote an endpoint or monitoring node into the Servers table
  const handlePromote = async (record) => {
    const nameField = type === "endpoints" ? "endpoint_name" : "node_name";
    const assetName = record[nameField] || "";
    const today     = new Date().toISOString().split("T")[0];

    // When promoting from endpoint — check monitoring_nodes for a match
    // When promoting from monitoring node — check endpoints for a match
    let monitoringEnabled  = "N";
    let monitoringTool     = "";
    let crowdstrikeEnabled = "N";

    if (type === "endpoints") {
      // This is definitively a CrowdStrike-monitored endpoint
      crowdstrikeEnabled = record.crowdstrike_installed || "Y";
      // Check if it also exists in monitoring nodes
      if (assetName) {
        const { data: monMatches } = await supabase
          .from("monitoring_nodes")
          .select("node_name,monitoring_tool,monitored")
          .eq("customer_id", record.customer_id || "")
          .ilike("node_name", assetName);
        if (monMatches && monMatches.length > 0) {
          monitoringEnabled = "Y";
          monitoringTool    = monMatches[0].monitoring_tool || "Netdata";
        }
      }
    }

    if (type === "monitoring") {
      // Promoting from monitoring node — it is by definition monitored
      monitoringEnabled  = "Y";
      monitoringTool     = record.monitoring_tool || "Netdata";
      // Check if it also exists in endpoints (CrowdStrike)
      if (assetName) {
        const { data: epMatches } = await supabase
          .from("endpoints")
          .select("endpoint_name,crowdstrike_installed")
          .eq("customer_id", record.customer_id || "")
          .ilike("endpoint_name", assetName);
        if (epMatches && epMatches.length > 0) {
          crowdstrikeEnabled = epMatches[0].crowdstrike_installed || "Y";
        }
      }
    }

    // Map OS platform to server schema options
    const rawOs = (record.os || record.platform || "").trim();
    let mappedOs = "Unknown";
    if (/windows/i.test(rawOs))    mappedOs = "Windows";
    else if (/linux/i.test(rawOs)) mappedOs = "Linux";

    const prefill = {
      customer_id:         record.customer_id  || "",
      server_name:         assetName,
      local_ip:            record.local_ip      || "",
      os:                  mappedOs,
      os_version:          record.os_version    || "",
      crowdstrike_enabled: crowdstrikeEnabled,
      monitoring_enabled:  monitoringEnabled,
      monitoring_tool:     monitoringTool,
      source:              record.source        || type,
      last_updated:        today,
      // Select fields with no matching data — sensible defaults
      environment:         "Unknown",
      erp_association:     "Unknown",
      criticality_tier:    "Unknown",
      management_scope:    "None",
    };

    setPromotePrefill(prefill);
  };

  const handleBulkPromote = async (ids) => {
    if (!ids || ids.length === 0) return;
    const today  = new Date().toISOString().split("T")[0];
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const nameField = type === "endpoints" ? "endpoint_name" : "node_name";

    // ── Fetch all three tables upfront (one query each) ──────────────────────
    const [{ data: existingServers }, { data: allEndpoints }, { data: allNodes }] = await Promise.all([
      supabase.from("servers").select("server_name,customer_id"),
      supabase.from("endpoints").select("endpoint_name,customer_id,crowdstrike_installed"),
      supabase.from("monitoring_nodes").select("node_name,customer_id,monitoring_tool"),
    ]);

    // Build lookup sets for fast in-memory matching
    // Key: "lowercase_name||customer_id"
    const serverKey    = (name, cid) => `${(name || "").toLowerCase()}||${cid || ""}`;
    const existingSet  = new Set((existingServers  || []).map(s => serverKey(s.server_name,  s.customer_id)));
    const epMap        = {};  // key → { crowdstrike_installed }
    const nodeMap      = {};  // key → { monitoring_tool }
    (allEndpoints || []).forEach(e => { epMap[serverKey(e.endpoint_name, e.customer_id)] = e; });
    (allNodes     || []).forEach(n => { nodeMap[serverKey(n.node_name,   n.customer_id)] = n; });

    // ── Process selected records ─────────────────────────────────────────────
    // Query max server ID once before the loop — increment in memory to avoid duplicates
    const { data: lastSrvData } = await supabase.from("servers")
      .select("server_id").order("server_id", { ascending: false }).limit(1);
    const lastSrvId = lastSrvData?.[0]?.server_id || "SRV-00000";
    let nextServerNum = parseInt(lastSrvId.replace("SRV-", "").replace(/\D/g, "")) || 0;

    let inserted = 0, skipped = 0;

    for (const id of ids) {
      const record   = records.find(r => r.id === id);
      if (!record) continue;

      const assetName = record[nameField] || "";
      const custId    = record.customer_id || "";
      const key       = serverKey(assetName, custId);

      // Skip if already in servers table
      if (existingSet.has(key)) { skipped++; continue; }

      // Resolve monitoring / crowdstrike flags via in-memory lookup
      let monitoringEnabled  = "N";
      let monitoringTool     = "";
      let crowdstrikeEnabled = "N";

      if (type === "endpoints") {
        crowdstrikeEnabled = record.crowdstrike_installed || "Y";
        const nodeMatch = nodeMap[key];
        if (nodeMatch) {
          monitoringEnabled = "Y";
          monitoringTool    = nodeMatch.monitoring_tool || "Netdata";
        }
      } else {
        // monitoring node
        monitoringEnabled  = "Y";
        monitoringTool     = record.monitoring_tool || "Netdata";
        const epMatch = epMap[key];
        if (epMatch) crowdstrikeEnabled = epMatch.crowdstrike_installed || "Y";
      }

      // Map OS
      const rawOs = (record.os || record.platform || "").trim();
      const mappedOs = /windows/i.test(rawOs) ? "Windows" : /linux/i.test(rawOs) ? "Linux" : "Unknown";

      nextServerNum++;
      const serverId = `SRV-${String(nextServerNum).padStart(5, "0")}`;
      const payload  = {
        server_id:           serverId,
        customer_id:         custId,
        server_name:         assetName,
        local_ip:            record.local_ip   || "",
        os:                  mappedOs,
        os_version:          record.os_version || "",
        crowdstrike_enabled: crowdstrikeEnabled,
        monitoring_enabled:  monitoringEnabled,
        monitoring_tool:     monitoringTool,
        source:              record.source     || type,
        last_updated:        today,
        environment:         "Unknown",
        erp_association:     "Unknown",
        criticality_tier:    "Unknown",
        management_scope:    "None",
        import_status:       "staged",
        created_by:          userId,
        updated_by:          userId,
      };

      const { error } = await supabase.from("servers").insert(payload);
      if (error) { toast(`Insert failed for ${assetName}: ${error.message}`, "error"); continue; }
      await logAudit("INSERT", "servers", serverId, null, payload, userEmail);
      // Add to existingSet so duplicates within this same batch are also skipped
      existingSet.add(key);
      inserted++;
    }

    const msg = skipped > 0
      ? `✓ Promoted ${inserted} to Servers as staged${skipped > 0 ? ` (${skipped} skipped — already exist)` : ""}`
      : `✓ Promoted ${inserted} record${inserted !== 1 ? "s" : ""} to Servers as staged`;
    toast(msg, inserted > 0 ? "success" : "info");
    if (inserted > 0) load();
  };

  const handleDelete = async (record) => {
// eslint-disable-next-line no-restricted-globals
    if (!confirm(`Delete ${record[schema.idField]}? This cannot be undone.`)) return;
    const { error } = await supabase.from(schema.table).delete().eq("id", record.id);
    if (error) { toast("Delete failed: " + error.message, "error"); return; }
    await logAudit("DELETE", schema.table, record[schema.idField], record, null, userEmail);
    toast("Record deleted", "info");
    load();
  };

  const handleExport = async () => {
    const headers = schema.fields.map(f => f.key);
    downloadCSV(`EMS_${schema.label}_${new Date().toISOString().split("T")[0]}.csv`, headers, records);
    await supabase.from("export_log").insert({ exported_by: (await supabase.auth.getUser()).data.user?.id, exported_by_email: userEmail, export_type: type, record_count: records.length });
    await logAudit("EXPORT", schema.table, "bulk", null, { count: records.length }, userEmail);
    toast(`Exported ${records.length} records`, "success");
  };

  return (
    <div>
      <div className="page-header">
        <span style={{ fontSize: "1.8rem" }}>{schema.icon}</span>
        <div className="page-title">{schema.label}</div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={handleExport}>⬇ Export CSV</button>
          <button className="btn btn-secondary" onClick={() => generateTemplate(type)}>📋 Template</button>
          <button className="btn btn-primary" onClick={() => setModal("add")}>+ Add Record</button>
        </div>
      </div>
      <div className="page-content">
        {records.filter(r => r.import_status === "staged").length > 0 && (
          <div style={{ margin: "0 2rem 0", padding: "0.7rem 1.25rem", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: "var(--radius)", fontFamily: "var(--font-mono)", fontSize: "0.8rem", color: "var(--warning)", display: "flex", alignItems: "center", gap: "0.75rem" }}>
            ⏳ <strong>{records.filter(r => r.import_status === "staged").length}</strong> staged record{records.filter(r => r.import_status === "staged").length !== 1 ? "s" : ""} awaiting validation — review and click ✓ Activate to confirm each one
          </div>
        )}
        <TableView type={type} records={records} customerMap={customerMap} onEdit={r => setModal(r)} onDelete={handleDelete} onActivate={handleActivate} onBulkActivate={handleBulkActivate} onPromote={handlePromote} onBulkPromote={handleBulkPromote} isAdmin={isAdmin} canEdit={canEdit} loading={loading} />
      </div>
      {modal && (
        type === "customers"
          ? <CustomerModal customer={modal === "add" ? null : modal} onSave={handleSave} onClose={() => setModal(null)} />
          : <RecordModal type={type} record={modal === "add" ? null : modal} onSave={handleSave} onClose={() => setModal(null)} />
      )}
      {/* Promote to Server modal — opens a pre-filled Add Server form */}
      {promotePrefill && (
        <RecordModal
          type="servers"
          record={null}
          prefill={promotePrefill}
          onSave={handlePromoteSave}
          onClose={() => setPromotePrefill(null)}
        />
      )}
    </div>
  );
}

// ============================================================
// UPLOAD PAGE — with pre-flight review
// ============================================================
function UploadPage({ profile, userEmail, toast }) {
  const isAdmin    = profile?.role === "administrator";
  const isManager  = profile?.role === "manager";
  const canUpdate  = isAdmin || isManager; // only admins/managers may update via CSV

  const [dragging,           setDragging]           = useState(false);
  const [uploadType,         setUploadType]         = useState("auto");
  const [preflight,          setPreflight]          = useState(null);
  const [committing,         setCommitting]         = useState(false);
  const [lastResult,         setLastResult]         = useState(null);
  const [sysImportSource,    setSysImportSource]    = useState(null);
  const [sysImportPreflight, setSysImportPreflight] = useState(null);
  const [sysImportCommitting,setSysImportCommitting]= useState(false);
  const [importAliases,      setImportAliases]      = useState(null);

  // ── STEP 1: parse & analyse the file, build preflight report ──────────────
  const processFile = async (file) => {
    setLastResult(null);
    setPreflight(null);
    try {
      let rows;
      const isXLSX = file.name.toLowerCase().endsWith(".xlsx") || file.name.toLowerCase().endsWith(".xls");

      if (isXLSX) {
        // Load SheetJS if not already present
        if (!window.XLSX) {
          await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
            s.onload = resolve; s.onerror = reject;
            document.head.appendChild(s);
          });
        }
        const buffer = await file.arrayBuffer();
        const wb     = window.XLSX.read(buffer, { type: "array" });
        // Use the "Data" sheet if it exists, otherwise the first sheet
        const sheetName = wb.SheetNames.includes("Data") ? "Data" : wb.SheetNames[0];
        const ws   = wb.Sheets[sheetName];
        rows = window.XLSX.utils.sheet_to_json(ws, { defval: "" });
        // sheet_to_json returns objects keyed by header row — same shape as parseCSV
      } else {
        const text = await file.text();
        rows = parseCSV(text);
      }
      if (!rows.length) { toast("No data rows found in file", "error"); return; }

      const headers = Object.keys(rows[0]);
      const detected = detectCSVType(headers);
      const type = uploadType === "auto" ? detected : uploadType;
      if (!type) { toast("Cannot detect asset type — select manually above", "error"); return; }

      const schema = SCHEMA[type];
      const idField = schema.idField;
      const userId  = (await supabase.auth.getUser()).data.user?.id;

      // Map CSV rows to DB field names
      const mapped = rows
        .map(r => { const row = mapCSVRow(r, type); row.created_by = userId; row.updated_by = userId; return row; })
        .filter(r => Object.values(r).some(v => v && v !== userId));

      // ── Check 1: duplicates within the file itself ────────────────────────
      const seenIds = new Set();
      const inFileDupes = [];
      mapped.forEach(r => {
        const id = r[idField];
        if (id) { if (seenIds.has(id)) inFileDupes.push(id); else seenIds.add(id); }
      });
      if (inFileDupes.length) {
        toast(`Upload blocked: duplicate IDs within the file — ${inFileDupes.join(", ")}`, "error");
        return;
      }

      // ── Check 2: fetch all existing records for this table ────────────────
      const { data: existing, error: fetchErr } = await supabase
        .from(schema.table)
        .select(`id,${idField},customer_id`);
      if (fetchErr) { toast("Could not fetch existing records: " + fetchErr.message, "error"); return; }
      const existingMap = {};  // idField value → { id, customer_id }
      (existing || []).forEach(r => { existingMap[r[idField]] = r; });

      // ── Check 3 (customers only): name-based duplicate detection ────────────
      // Catches cases where a row has no ID but matches an existing customer name
      let existingNameMap = {};  // lowercase name → { customer_id, customer_name }
      if (type === "customers") {
        const { data: existingNames } = await supabase
          .from("customers").select("customer_id,customer_name");
        (existingNames || []).forEach(c => {
          existingNameMap[c.customer_name.toLowerCase().trim()] = c;
        });
      }

      // ── Categorise every row ──────────────────────────────────────────────
      const toInsert  = [];   // brand-new records (no ID, or ID not in DB)
      const toUpdate  = [];   // existing record, same customer — manager/admin only
      const blocked   = [];   // existing record, different customer — always blocked
      const noId      = [];   // rows with blank ID — will be auto-assigned
      const nameDupes = [];   // rows with no ID but name matches existing customer

      // Engagements are always insert-only via CSV
      const insertOnly = (type === "engagements");

      for (const row of mapped) {
        const rowId    = row[idField]?.trim();
        const rowCust  = row.customer_id?.trim() || null;

        if (!rowId) {
          // Blank ID — check name match for customers before auto-assigning
          if (type === "customers" && row.customer_name) {
            const nameKey = row.customer_name.toLowerCase().trim();
            const nameMatch = existingNameMap[nameKey];
            if (nameMatch) {
              nameDupes.push({ row, existingId: nameMatch.customer_id, existingName: nameMatch.customer_name });
              continue;
            }
          }
          noId.push(row);
          continue;
        }

        const existingRec = existingMap[rowId];

        if (!existingRec) {
          // ID not in DB → insert
          toInsert.push(row);
          continue;
        }

        if (insertOnly) {
          // Engagements: even if ID matches, treat as blocked
          blocked.push({ row, reason: "Engagement updates must be done via the console", existingCustomer: existingRec.customer_id });
          continue;
        }

        if (existingRec.customer_id && rowCust && existingRec.customer_id !== rowCust) {
          // ID exists but belongs to a different customer → always blocked
          blocked.push({ row, reason: `ID belongs to customer ${existingRec.customer_id}`, existingCustomer: existingRec.customer_id });
          continue;
        }

        if (!canUpdate) {
          // Consultant trying to update → blocked
          blocked.push({ row, reason: "Consultants may not update existing records via CSV", existingCustomer: existingRec.customer_id });
          continue;
        }

        // Manager/admin, same customer → update allowed
        toUpdate.push({ row, existingId: existingRec.id, existingIdField: rowId });
      }

      // If name duplicates exist for customers → block with explanation
      if (nameDupes.length) {
        const dupeList = nameDupes.map(d => d.row.customer_name).join(", ");
        setPreflight({
          type, schema, idField, blocked: true,
          blockedRows: nameDupes.map(d => ({
            row: d.row,
            reason: `Name already exists as ${d.existingId} — add the ID to update, or rename to insert as new`,
            existingCustomer: d.existingId,
          }))
        });
        return;
      }

      // If ANY blocked rows exist → block the entire upload
      if (blocked.length) {
        setPreflight({ type, schema, idField, blockedRows: blocked, blocked: true });
        return;
      }

      // Fetch full existing records for diff view on updates
      let updateDiffs = [];
      if (toUpdate.length) {
        const updateIds = toUpdate.map(u => u.existingId);
        const { data: fullExisting } = await supabase
          .from(schema.table)
          .select("*")
          .in("id", updateIds);
        const fullMap = {};
        (fullExisting || []).forEach(r => { fullMap[r.id] = r; });
        updateDiffs = toUpdate.map(u => ({
          row: u.row,
          existing: fullMap[u.existingId] || {},
          existingId: u.existingId,
          idFieldValue: u.existingIdField,
          // Compute changed fields
          changes: Object.entries(u.row)
            .filter(([k, v]) => !["created_by","updated_by","id","created_at","updated_at"].includes(k)
              && String(v ?? "") !== String(fullMap[u.existingId]?.[k] ?? ""))
            .map(([k, v]) => ({ field: k, oldVal: fullMap[u.existingId]?.[k] ?? "", newVal: v }))
        }));
      }

      setPreflight({
        type, schema, idField,
        toInsert, noId,
        toUpdate: updateDiffs,
        blocked: false,
        userId,
        fileName: file.name,
      });

    } catch (e) { toast("Error reading file: " + e.message, "error"); }
  };

  // ── STEP 2: user confirmed — commit to database ────────────────────────────
  const commitInFlight = useRef(false);  // synchronous guard — state updates are async
  const commitUpload = async () => {
    if (!preflight || preflight.blocked) return;
    if (commitInFlight.current) return;  // hard stop — already running
    commitInFlight.current = true;
    setCommitting(true);
    const { type, schema, toInsert, noId, toUpdate, userId } = preflight;
    let inserted = 0, updated = 0;

    try {
      // Auto-assign IDs to blank-ID rows — query DB once, increment in memory
      // (avoids duplicate key when multiple blank-ID rows are in the same batch)
      const assignedRows = [];
      if (noId.length > 0) {
        const { data: lastRow } = await supabase
          .from(schema.table)
          .select(schema.idField)
          .order(schema.idField, { ascending: false })
          .limit(1);
        const lastId  = lastRow?.[0]?.[schema.idField] || "";
        const match   = lastId.match(/\d+$/);
        let nextNum   = match ? parseInt(match[0]) + 1 : 1;
        for (const row of noId) {
          const newId = `${schema.idPrefix}-${String(nextNum).padStart(5, "0")}`;
          nextNum++;
          assignedRows.push({ ...row, [schema.idField]: newId });
        }
      }

      // Inserts (new IDs + auto-assigned)
      const allInserts = [...toInsert, ...assignedRows];
      if (allInserts.length) {
        const { data, error } = await supabase.from(schema.table).insert(allInserts).select();
        if (error) { toast("Insert failed: " + error.message, "error"); commitInFlight.current = false; setCommitting(false); return; }
        inserted = data?.length || allInserts.length;
        await logAudit("BULK_INSERT", schema.table, "bulk", null, { count: inserted }, userEmail);
      }

      // Updates (managers/admins only)
      for (const u of toUpdate) {
        const payload = { ...u.row, updated_by: userId };
        delete payload.id; delete payload.created_at; delete payload.updated_at;
        const { error } = await supabase.from(schema.table).update(payload).eq("id", u.existingId);
        if (error) { toast(`Update failed for ${u.idFieldValue}: ${error.message}`, "error"); commitInFlight.current = false; setCommitting(false); return; }
        await logAudit("BULK_UPDATE", schema.table, u.idFieldValue, u.existing, u.row, userEmail);
        updated++;
      }

      const total = inserted + updated;
      setLastResult({ type, inserted, updated, label: schema.label });
      toast(`✓ ${inserted} inserted, ${updated} updated in ${schema.label}`, "success");
      setPreflight(null);
    } catch (e) { toast("Commit error: " + e.message, "error"); }
    commitInFlight.current = false;
    setCommitting(false);
  };

  // ── PREFLIGHT REVIEW SCREEN ───────────────────────────────────────────────
  if (preflight) {
    const { schema, toInsert, noId, toUpdate, blocked: isBlocked, type } = preflight;
    const blockedRows = preflight.blockedRows || [];

    // Re-derive blocked from the preflight object (stored differently above)
    // Actually grab from the analysis — rerun if needed
    // Note: blocked rows are stored in preflight when blocked=true
    return (
      <div>
        <div className="page-header">
          <div className="page-title">📋 Upload Review — {schema.label}</div>
          <div className="page-actions">
            <button className="btn btn-secondary" onClick={() => setPreflight(null)}>← Back</button>
            {!isBlocked && (
              <button className="btn btn-primary" onClick={commitUpload} disabled={committing}>
                {committing ? <span className="loading-spinner" /> : `✓ Confirm & Import`}
              </button>
            )}
          </div>
        </div>
        <div className="page-content">

          {/* BLOCKED UPLOAD */}
          {isBlocked && (
            <div>
              <div style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: "var(--radius)", padding: "1.25rem", marginBottom: "1.5rem" }}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--danger)", marginBottom: "1rem" }}>
                  🚫 Upload Blocked — {preflight.blockedRows?.length} Issue{preflight.blockedRows?.length !== 1 ? "s" : ""} Must Be Resolved Before Continuing
                </div>
                <div className="table-wrap">
                  <table>
                    <thead><tr>
                      <th>{preflight.idField}</th>
                      <th>Customer in File</th>
                      <th>Reason Blocked</th>
                    </tr></thead>
                    <tbody>
                      {(preflight.blockedRows || []).map((b, i) => (
                        <tr key={i}>
                          <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem", color: "var(--danger)" }}>{b.row[preflight.idField] || "—"}</td>
                          <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem" }}>{b.row.customer_id || "—"}</td>
                          <td style={{ fontSize: "0.82rem", color: "var(--warning)" }}>{b.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: "1rem", fontSize: "0.84rem", color: "var(--text2)" }}>
                  Fix all issues in your CSV file and upload again. No records have been saved.
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button className="btn btn-secondary" onClick={() => setPreflight(null)}>← Upload Different File</button>
              </div>
            </div>
          )}

          {!isBlocked && (
            <>
              {/* SUMMARY COUNTS */}
              <div style={{ display: "flex", gap: "1rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
                {[
                  { label: "New Records to Insert", count: toInsert.length + noId.length, color: "var(--success)", icon: "✅" },
                  { label: "Records to Update", count: toUpdate.length, color: "var(--info)", icon: "🔄" },
                  { label: "Rows with No ID (auto-assign)", count: noId.length, color: "var(--warning)", icon: "🔑" },
                ].map((s, i) => (
                  <div key={i} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "0.85rem 1.25rem", minWidth: 180 }}>
                    <div style={{ fontSize: "1.4rem", marginBottom: "0.25rem" }}>{s.icon}</div>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: "1.2rem", fontWeight: 700, color: s.color }}>{s.count}</div>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.7rem", color: "var(--text2)", marginTop: "0.2rem" }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* NO-ID ROWS WARNING */}
              {noId.length > 0 && (
                <div style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: "var(--radius)", padding: "1rem", marginBottom: "1.25rem" }}>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--warning)", marginBottom: "0.6rem" }}>🔑 {noId.length} Row{noId.length !== 1 ? "s" : ""} With No ID — Will Be Auto-Assigned</div>
                  {noId.map((row, i) => (
                    <div key={i} style={{ fontFamily: "var(--font-mono)", fontSize: "0.78rem", color: "var(--text2)", padding: "0.2rem 0" }}>
                      Row {i + 1}: {row.customer_id ? `Customer ${row.customer_id}` : "No customer"} — {row[Object.keys(row).find(k => k.includes("name"))] || "unnamed record"}
                    </div>
                  ))}
                </div>
              )}

              {/* NEW INSERTS */}
              {toInsert.length > 0 && (
                <div style={{ marginBottom: "1.5rem" }}>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--success)", marginBottom: "0.75rem" }}>✅ {toInsert.length} New Record{toInsert.length !== 1 ? "s" : ""} to Insert</div>
                  <div className="table-wrap">
                    <table>
                      <thead><tr>
                        <th>{schema.idField}</th>
                        <th>Customer</th>
                        <th>Name</th>
                      </tr></thead>
                      <tbody>
                        {toInsert.map((row, i) => (
                          <tr key={i}>
                            <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem" }}>{row[schema.idField] || "—"}</td>
                            <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem" }}>{row.customer_id || "—"}</td>
                            <td>{row[schema.nameField] || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* UPDATES WITH FIELD-LEVEL DIFF */}
              {toUpdate.length > 0 && (
                <div style={{ marginBottom: "1.5rem" }}>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--info)", marginBottom: "0.75rem" }}>🔄 {toUpdate.length} Existing Record{toUpdate.length !== 1 ? "s" : ""} to Update</div>
                  {toUpdate.map((u, i) => (
                    <div key={i} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "1rem", marginBottom: "0.75rem" }}>
                      <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.8rem", fontWeight: 600, color: "var(--accent2)", marginBottom: "0.6rem" }}>
                        {u.idFieldValue} — {u.row[schema.nameField] || ""} (Customer: {u.row.customer_id || "—"})
                      </div>
                      {u.changes.length === 0 ? (
                        <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem", color: "var(--text3)" }}>No field changes detected</div>
                      ) : (
                        <table style={{ width: "100%", fontSize: "0.78rem", fontFamily: "var(--font-mono)" }}>
                          <thead><tr>
                            <th style={{ textAlign: "left", padding: "0.3rem 0.5rem", color: "var(--text2)", fontWeight: 600 }}>Field</th>
                            <th style={{ textAlign: "left", padding: "0.3rem 0.5rem", color: "var(--danger)", fontWeight: 600 }}>Current Value</th>
                            <th style={{ textAlign: "left", padding: "0.3rem 0.5rem", color: "var(--success)", fontWeight: 600 }}>New Value</th>
                          </tr></thead>
                          <tbody>
                            {u.changes.map((c, j) => (
                              <tr key={j} style={{ borderTop: "1px solid var(--border)" }}>
                                <td style={{ padding: "0.3rem 0.5rem", color: "var(--text2)" }}>{c.field}</td>
                                <td style={{ padding: "0.3rem 0.5rem", color: "var(--danger)" }}>{String(c.oldVal) || <span style={{ color: "var(--text3)" }}>—</span>}</td>
                                <td style={{ padding: "0.3rem 0.5rem", color: "var(--success)" }}>{String(c.newVal) || <span style={{ color: "var(--text3)" }}>—</span>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* CONFIRM BUTTON AT BOTTOM TOO */}
              <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end", paddingTop: "1rem", borderTop: "1px solid var(--border)" }}>
                <button className="btn btn-secondary" onClick={() => setPreflight(null)}>← Cancel, Upload Different File</button>
                <button className="btn btn-primary" onClick={commitUpload} disabled={committing}>
                  {committing ? <span className="loading-spinner" /> : `✓ Confirm & Import ${toInsert.length + noId.length + toUpdate.length} Records`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── SYSTEM IMPORT: CrowdStrike / Netdata ─────────────────────────────────
  // ── Field mappings for system imports ──────────────────────────────────────
  // CrowdStrike: strips "EMS > " / "EMS>" prefix; bare "EMS" = internal
  const CS_TYPE_MAP = {
    "Server": "Server", "Domain Controller": "Server", "Workstation": "Workstation"
  };

  // Netdata abbreviation lookup — loaded from system_import_aliases table
  // (replaces hardcoded lookup; managed by admins/managers in Admin Tools)
  const loadImportAliases = async () => {
    if (importAliases !== null) return importAliases;
    const { data } = await supabase
      .from("system_import_aliases")
      .select("alias,customer_name,source");
    const map = {};
    (data || []).forEach(r => {
      // key: "ALIAS|source" and "ALIAS|any"
      map[`${r.alias.toUpperCase()}|${r.source}`] = r.customer_name;
      if (r.source === "any") map[`${r.alias.toUpperCase()}|netdata`] = r.customer_name;
    });
    setImportAliases(map);
    return map;
  };

  const resolveAlias = (alias, source, aliasMap) => {
    if (!alias || !aliasMap) return null;
    const key = alias.toUpperCase().trim();
    return aliasMap[`${key}|${source}`] || aliasMap[`${key}|any`] || null;
  };

  const saveAlias = async (alias, customerName, source) => {
    if (!alias || !customerName) return;
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const { error } = await supabase.from("system_import_aliases")
      .upsert({ alias: alias.toUpperCase().trim(), customer_name: customerName, source, created_by: userId },
               { onConflict: "alias,source" });
    if (error) { toast("Could not save alias: " + error.message, "error"); return; }
    // Bust the cache so next import picks up the new entry
    setImportAliases(null);
    toast(`Alias saved: ${alias.toUpperCase()} → ${customerName}`, "success");
  };
  // Netdata type -> { node_type, sla_tier hint }
  const ND_TYPE_MAP = {
    "Linux Prod":     { node_type: "Server",  monitored: "Y", sla_tier: "Prod" },
    "Linux Non Prod": { node_type: "Server",  monitored: "Y", sla_tier: "Non-Prod" },
    "Win Prod":       { node_type: "Server",  monitored: "Y", sla_tier: "Prod" },
    "Win Non Prod":   { node_type: "Server",  monitored: "Y", sla_tier: "Non-Prod" },
    "Network":        { node_type: "Network", monitored: "Y", sla_tier: null },
    "Other":          { node_type: "Other",   monitored: "Y", sla_tier: null },
  };

  function parseCrowdStrikeCustomer(raw) {
    if (!raw) return { name: null, isInternal: false };
    const s = raw.trim();
    if (s.toUpperCase() === "EMS") return { name: "EMS Internal", isInternal: true };
    if (s.toUpperCase().startsWith("EMS > ")) return { name: s.substring(6).trim(), isInternal: false };
    if (s.toUpperCase().startsWith("EMS>"))  return { name: s.substring(4).trim(), isInternal: false };
    return { name: s, isInternal: false };
  }

  function parseNetdataAgent(agent) {
    // Format: "hostname.domain.tld (ip)" or "hostname (ip)" or "hostname"
    const s = String(agent || "").trim();
    const ipMatch = s.match(/\((\d+\.\d+\.\d+\.\d+)\)/);
    const ip = ipMatch ? ipMatch[1] : null;
    const namePart = s.replace(/\s*\(.*?\)/, "").trim();
    const parts = namePart.split(".");
    const hostname = parts[0];
    const domain   = parts.length > 1 ? parts.slice(1).join(".") : null;
    return { hostname, domain, ip };
  }

  const processSystemImport = async (file, source) => {
    setSysImportPreflight(null);
    try {
      let rawRows = [];

      if (source === "crowdstrike") {
        // CSV file
        const text = await file.text();
        rawRows = parseCSV(text);
        if (!rawRows.length) { toast("No data rows found in CrowdStrike export", "error"); return; }
      } else {
        // XLSX file — read with SheetJS if available, else tell user to export as CSV
        if (typeof XLSX !== "undefined") {
          const buffer = await file.arrayBuffer();
          const wb = window.XLSX.read(buffer, { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          rawRows = window.XLSX.utils.sheet_to_json(ws, { defval: "" });
        } else {
          // Fallback: ask user to save as CSV
          toast("Please export the Netdata file as CSV (.csv) and re-upload", "error");
          return;
        }
      }

      // Load alias table and existing customers
      const aliasMap = await loadImportAliases();
      const { data: allCustomers } = await supabase.from("customers").select("id,customer_id,customer_name");
      const customers = allCustomers || [];

      const table    = source === "crowdstrike" ? "endpoints"        : "monitoring_nodes";
      const idField  = source === "crowdstrike" ? "endpoint_id"      : "node_id";
      const idPrefix = source === "crowdstrike" ? "EP"               : "MON";
      const nameCol  = source === "crowdstrike" ? "endpoint_name"    : "node_name";

      // Fetch existing records for duplicate detection by name
      const { data: existingAssets } = await supabase
        .from(table).select(`id,${idField},customer_id,${nameCol},local_ip`);
      const existingByName = {};
      (existingAssets || []).forEach(r => {
        const key = (r[nameCol] || "").toLowerCase().trim();
        if (key) existingByName[key] = r;
      });

      const preflightRows = [];
      const newCustomersMap = {};

      for (const row of rawRows) {
        let resolvedCustomerName, assetName, assetPayload, isInternal = false;

        if (source === "crowdstrike") {
          // ── CrowdStrike field extraction ──────────────────────────────────
          const parsed = parseCrowdStrikeCustomer(row["Customer Name"] || "");
          resolvedCustomerName = parsed.name;
          isInternal = parsed.isInternal;
          assetName  = (row["Hostname"] || "").trim();
          const rawType = (row["Type"] || "").trim();
          assetPayload = {
            crowdstrike_host_id: (row["Host ID"] || "").trim() || null,
            platform:            (row["Platform"] || "").trim() || null,
            os_version:          (row["OS Version"] || "").trim() || null,
            local_ip:            (row["Local IP"] || "").trim() || null,
            last_check_in:       row["Last Seen"] ? row["Last Seen"].substring(0, 10) : null,
            endpoint_type:       CS_TYPE_MAP[rawType] || "Other",
            crowdstrike_installed: "Y",
            alert_triggered:     "N",
            escalated_to_ems:    "N",
            os:                  (row["Platform"] || "").trim() || null,
          };
        } else {
          // ── Netdata field extraction ──────────────────────────────────────
          const abbr = (row["Customer"] || row["customer"] || "").trim().toUpperCase();
          resolvedCustomerName = resolveAlias(abbr, "netdata", aliasMap) || abbr || null;
          const { hostname, domain, ip } = parseNetdataAgent(row["agent"] || row["Agent"] || "");
          assetName = hostname || "";
          const rawType  = (row["Type"] || row["type"] || "").trim();
          const typeInfo = ND_TYPE_MAP[rawType] || { node_type: "Other", monitored: "Y", sla_tier: null };
          assetPayload = {
            local_ip:        ip || null,
            node_type:       typeInfo.node_type,
            monitored:       typeInfo.monitored,
            monitoring_tool: "Netdata",
            sla_tier:        typeInfo.sla_tier || null,
            last_seen:       new Date().toISOString().split("T")[0],
          };
        }

        if (!assetName) continue; // skip blank rows

        // ── Customer matching ─────────────────────────────────────────────
        const match = matchCustomer(resolvedCustomerName, customers);
        let custAction, resolvedCustomerId;

        if (isInternal) {
          // EMS internal — will match or create "EMS Internal" customer
          const internalMatch = customers.find(c =>
            c.customer_name?.toLowerCase() === "ems internal"
          );
          custAction = internalMatch ? "exact" : "new";
          resolvedCustomerId = internalMatch?.customer_id || null;
        } else if (match.type === "exact") {
          custAction = "exact";
          resolvedCustomerId = match.customer.customer_id;
        } else if (match.type === "fuzzy") {
          custAction = "fuzzy";
          resolvedCustomerId = null;
        } else {
          custAction = "new";
          resolvedCustomerId = null;
          if (resolvedCustomerName && !newCustomersMap[resolvedCustomerName.toLowerCase()]) {
            newCustomersMap[resolvedCustomerName.toLowerCase()] = resolvedCustomerName;
          }
        }

        // ── Duplicate detection by hostname ──────────────────────────────
        const nameKey = assetName.toLowerCase().trim();
        const existingAsset = existingByName[nameKey] || null;

        preflightRows.push({
          row, assetName, rawCustName: resolvedCustomerName, isInternal,
          custAction, resolvedCustomerId, assetPayload,
          suggestedCustomer: match.type === "fuzzy" ? match.customer : null,
          similarityScore:   match.score,
          existingAsset,
          source,
        });
      }

      if (!preflightRows.length) { toast("No valid rows found after parsing", "error"); return; }

      setSysImportPreflight({
        source, table, idField, idPrefix, nameCol,
        rows: preflightRows,
        newCustomers: Object.values(newCustomersMap),
        confirmedMatches: Object.fromEntries(
          preflightRows
            .filter(r => r.custAction === "fuzzy")
            .map(r => [r.rawCustName, r.suggestedCustomer?.customer_id || "__new__"])
        ),
        // Track which fuzzy/new mappings the user wants saved as aliases
        // key: rawCustName (abbr), value: true/false
        saveAsAlias: Object.fromEntries(
          preflightRows
            .filter(r => r.custAction === "fuzzy" || r.custAction === "new")
            .map(r => [r.rawCustName, true])  // default on — most of the time you want to save
        ),
      });

    } catch (e) { toast("Error reading file: " + e.message, "error"); }
  };

  const commitSystemImport = async () => {
    if (!sysImportPreflight) return;
    setSysImportCommitting(true);
    const { source, table, idField, idPrefix, nameCol, rows, confirmedMatches } = sysImportPreflight;
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const today  = new Date().toISOString().split("T")[0];

    try {
      // Step 1: create new placeholder customers
      const createdCustomers = {};
      const { data: allExistingCust } = await supabase.from("customers").select("customer_id,customer_name");
      const existingCustNames = new Set((allExistingCust || []).map(c => c.customer_name?.toLowerCase().trim()));

      for (const row of rows) {
        if (row.custAction !== "new") continue;
        const name = row.rawCustName;
        if (!name || existingCustNames.has(name.toLowerCase().trim())) continue;
        if (createdCustomers[name.toLowerCase()]) continue;

        // Generate customer ID
        const { data: lastCust } = await supabase.from("customers")
          .select("customer_id").order("customer_id", { ascending: false }).limit(1);
        const lastId   = lastCust?.[0]?.customer_id || "CMI-00000";
        const lastNum  = parseInt(lastId.replace("CMI-","")) || 0;
        const newCustId = `CMI-${String(lastNum + 1 + Object.keys(createdCustomers).length).padStart(5,"0")}`;

        const { data: newCust, error: custErr } = await supabase.from("customers").insert({
          customer_id: newCustId,
          customer_name: name,
          source,
          import_status: "staged",
          active: "Yes",
          created_by: userId,
          updated_by: userId,
        }).select().single();

        if (custErr) { toast(`Could not create customer "${name}": ${custErr.message}`, "error"); setSysImportCommitting(false); return; }
        createdCustomers[name.toLowerCase()] = newCust.customer_id;
        existingCustNames.add(name.toLowerCase().trim());
        await logAudit("SYSTEM_IMPORT_CUSTOMER", "customers", newCustId, null, newCust, userEmail);
      }

      // Re-fetch customers so we have all IDs including just-created ones
      const { data: freshCustomers } = await supabase.from("customers").select("id,customer_id,customer_name");

      // Step 2: upsert assets using pre-built assetPayload from preflight
      let inserted = 0, updated = 0;

      // Get current max ID once to avoid race conditions
      const { data: lastAssetData } = await supabase.from(table)
        .select(idField).order(idField, { ascending: false }).limit(1);
      const lastIdVal = lastAssetData?.[0]?.[idField] || `${idPrefix}-00000`;
      let nextNum = parseInt(lastIdVal.replace(`${idPrefix}-`, "").replace(/\D/g, "")) || 0;

      for (const r of rows) {
        // Resolve customer ID
        let custId = r.resolvedCustomerId;
        if (!custId && r.custAction === "fuzzy") {
          const confirmed = confirmedMatches[r.rawCustName];
          custId = (confirmed === "__new__")
            ? (createdCustomers[r.rawCustName?.toLowerCase()] || null)
            : (confirmed || null);
        }
        if (!custId && (r.custAction === "new" || r.isInternal)) {
          custId = createdCustomers[r.rawCustName?.toLowerCase()] || null;
        }

        // Merge pre-built payload from processSystemImport with commit-time fields
        const payload = {
          ...(r.assetPayload || {}),
          customer_id:   custId || null,
          [nameCol]:     r.assetName,
          source,
          import_status: "staged",
          last_updated:  today,
          updated_by:    userId,
        };

        if (r.existingAsset) {
          // Update existing staged record — preserve created_by
          delete payload.created_by;
          const { error } = await supabase.from(table).update(payload).eq("id", r.existingAsset.id);
          if (error) { toast(`Update failed for ${r.assetName}: ${error.message}`, "error"); continue; }
          updated++;
        } else {
          // Insert new record with generated ID
          nextNum++;
          payload[idField]   = `${idPrefix}-${String(nextNum).padStart(5, "0")}`;
          payload.created_by = userId;
          const { error } = await supabase.from(table).insert(payload);
          if (error) { toast(`Insert failed for ${r.assetName}: ${error.message}`, "error"); continue; }
          inserted++;
        }
      }

      // Save any checked "save as alias" mappings
      const { saveAsAlias, confirmedMatches: cm } = sysImportPreflight;
      for (const r of rows) {
        const abbr = r.rawCustName;
        if (!abbr || !saveAsAlias?.[abbr]) continue;
        // Determine the customer name that was resolved for this abbreviation
        let resolvedName = null;
        if (r.custAction === "exact" || r.isInternal) {
          resolvedName = r.rawCustName; // already full name
        } else if (r.custAction === "fuzzy") {
          const confirmedId = cm[abbr];
          if (confirmedId && confirmedId !== "__new__") {
            const cust = (await supabase.from("customers").select("customer_name").eq("customer_id", confirmedId).single()).data;
            resolvedName = cust?.customer_name || null;
          } else {
            resolvedName = createdCustomers[abbr?.toLowerCase()]
              ? abbr  // newly created — use abbr as alias for the created name
              : null;
          }
        } else if (r.custAction === "new") {
          resolvedName = abbr; // abbr was already resolved to full name via lookup or as-is
        }
        if (resolvedName) await saveAlias(abbr, resolvedName, source);
      }

      await logAudit("SYSTEM_IMPORT", table, "bulk", null, { source, inserted, updated }, userEmail);
      toast(`✓ System import complete — ${inserted} inserted, ${updated} updated`, "success");
      setSysImportPreflight(null);
      setSysImportSource(null);
      setLastResult({ type: source, inserted, updated, label: source === "crowdstrike" ? "CrowdStrike Endpoints" : "Netdata Monitoring Nodes" });
    } catch (e) { toast("Import error: " + e.message, "error"); }
    setSysImportCommitting(false);
  };

  // ── SYSTEM IMPORT PREFLIGHT SCREEN ───────────────────────────────────────
  if (sysImportPreflight) {
    const { source, rows, newCustomers, confirmedMatches } = sysImportPreflight;
    const exactRows  = rows.filter(r => r.custAction === "exact");
    const fuzzyRows  = rows.filter(r => r.custAction === "fuzzy");
    const newCustRows = rows.filter(r => r.custAction === "new");
    const dupRows    = rows.filter(r => r.existingAsset);

    return (
      <div>
        <div className="page-header">
          <div className="page-title">{source === "crowdstrike" ? "🛡️ CrowdStrike" : "📡 Netdata"} Import Review</div>
          <div className="page-actions">
            <button className="btn btn-secondary" onClick={() => setSysImportPreflight(null)}>← Back</button>
            <button className="btn btn-primary" onClick={commitSystemImport} disabled={sysImportCommitting}>
              {sysImportCommitting ? <span className="loading-spinner" /> : `✓ Confirm & Import ${rows.length} Records`}
            </button>
          </div>
        </div>
        <div className="page-content">

          {/* SUMMARY */}
          <div style={{ display: "flex", gap: "1rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
            {[
              { label: "Exact Customer Match", count: exactRows.length, color: "var(--success)", icon: "✅" },
              { label: "Suggested Match (confirm below)", count: fuzzyRows.length, color: "var(--warning)", icon: "🔶" },
              { label: "New Customer Will Be Created", count: newCustRows.length, color: "var(--info)", icon: "🆕" },
              { label: "Existing Asset (will update)", count: dupRows.length, color: "var(--text2)", icon: "🔄" },
            ].map((s, i) => (
              <div key={i} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "0.85rem 1.25rem", minWidth: 160 }}>
                <div style={{ fontSize: "1.4rem", marginBottom: "0.25rem" }}>{s.icon}</div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: "1.2rem", fontWeight: 700, color: s.color }}>{s.count}</div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", color: "var(--text2)", marginTop: "0.2rem" }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* FUZZY MATCHES — need confirmation */}
          {fuzzyRows.length > 0 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--warning)", marginBottom: "0.75rem" }}>
                🔶 Suggested Customer Matches — Please Confirm
              </div>
              <div style={{ background: "var(--bg2)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: "var(--radius)", overflow: "hidden" }}>
                <table style={{ width: "100%" }}>
                  <thead><tr>
                    <th style={{ padding: "0.6rem 1rem", textAlign: "left", fontFamily: "var(--font-mono)", fontSize: "0.72rem", color: "var(--text2)" }}>Name in Export</th>
                    <th style={{ padding: "0.6rem 1rem", textAlign: "left", fontFamily: "var(--font-mono)", fontSize: "0.72rem", color: "var(--text2)" }}>Confidence</th>
                    <th style={{ padding: "0.6rem 1rem", textAlign: "left", fontFamily: "var(--font-mono)", fontSize: "0.72rem", color: "var(--text2)" }}>Assign To</th>
                  </tr></thead>
                  <tbody>
                    {[...new Set(fuzzyRows.map(r => r.rawCustName))].map((name, i) => {
                      const fr = fuzzyRows.find(r => r.rawCustName === name);
                      return (
                        <tr key={i} style={{ borderTop: "1px solid var(--border)" }}>
                          <td style={{ padding: "0.6rem 1rem", fontFamily: "var(--font-mono)", fontSize: "0.8rem" }}>{name}</td>
                          <td style={{ padding: "0.6rem 1rem", fontFamily: "var(--font-mono)", fontSize: "0.8rem", color: "var(--warning)" }}>
                            {Math.round((fr?.similarityScore || 0) * 100)}% — suggested: {fr?.suggestedCustomer?.customer_name}
                          </td>
                          <td style={{ padding: "0.6rem 1rem" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                              <select className="form-select" style={{ fontSize: "0.8rem" }}
                                value={confirmedMatches[name] || fr?.suggestedCustomer?.customer_id || "__new__"}
                                onChange={e => setSysImportPreflight(p => ({
                                  ...p,
                                  confirmedMatches: { ...p.confirmedMatches, [name]: e.target.value }
                                }))}>
                                {fr?.suggestedCustomer && (
                                  <option value={fr.suggestedCustomer.customer_id}>
                                    ✓ {fr.suggestedCustomer.customer_name} (suggested)
                                  </option>
                                )}
                                <option value="__new__">🆕 Create new customer "{name}"</option>
                              </select>
                              <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontFamily: "var(--font-mono)", fontSize: "0.7rem", color: "var(--text2)", cursor: "pointer" }}>
                                <input type="checkbox"
                                  checked={sysImportPreflight.saveAsAlias?.[name] ?? true}
                                  onChange={e => setSysImportPreflight(p => ({
                                    ...p, saveAsAlias: { ...p.saveAsAlias, [name]: e.target.checked }
                                  }))} />
                                💾 Save "{name}" as alias for next time
                              </label>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* NEW CUSTOMERS */}
          {newCustRows.length > 0 && (
            <div style={{ marginBottom: "1.5rem", background: "rgba(99,102,241,0.05)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: "var(--radius)", padding: "1rem" }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--accent2)", marginBottom: "0.6rem" }}>
                🆕 {[...new Set(newCustRows.map(r => r.rawCustName))].length} New Placeholder Customer Record{[...new Set(newCustRows.map(r => r.rawCustName))].length !== 1 ? "s" : ""} Will Be Created (Staged)
              </div>
              {[...new Set(newCustRows.map(r => r.rawCustName))].map((name, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.3rem 0", borderTop: i > 0 ? "1px solid rgba(99,102,241,0.1)" : "none" }}>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.8rem", color: "var(--text2)" }}>
                    • {name || "(no customer name in export)"}
                  </span>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontFamily: "var(--font-mono)", fontSize: "0.7rem", color: "var(--text2)", cursor: "pointer", marginLeft: "1rem", whiteSpace: "nowrap" }}>
                    <input type="checkbox"
                      checked={sysImportPreflight.saveAsAlias?.[name] ?? true}
                      onChange={e => setSysImportPreflight(p => ({
                        ...p, saveAsAlias: { ...p.saveAsAlias, [name]: e.target.checked }
                      }))} />
                    💾 Save as alias for next time
                  </label>
                </div>
              ))}
              <div style={{ fontSize: "0.8rem", color: "var(--text3)", marginTop: "0.5rem" }}>
                These will be marked as staged. Managers and admins can update the name if needed.
              </div>
            </div>
          )}

          {/* ALL ROWS SUMMARY TABLE */}
          <div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text2)", marginBottom: "0.75rem" }}>
              All {rows.length} Records
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th>Asset Name</th>
                  <th>Customer in Export</th>
                  <th>Customer Match</th>
                  <th>Action</th>
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem" }}>{r.assetName || "—"}</td>
                      <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem" }}>{r.rawCustName || "—"}</td>
                      <td style={{ fontSize: "0.8rem" }}>
                        {r.custAction === "exact" && <span className="badge badge-green">✓ Exact</span>}
                        {r.custAction === "fuzzy" && <span className="badge badge-yellow">🔶 Fuzzy</span>}
                        {r.custAction === "new"   && <span className="badge badge-purple">🆕 New</span>}
                      </td>
                      <td style={{ fontSize: "0.8rem" }}>
                        {r.existingAsset ? <span className="badge badge-blue">🔄 Update</span> : <span className="badge badge-green">Insert</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end", paddingTop: "1rem", borderTop: "1px solid var(--border)", marginTop: "1rem" }}>
            <button className="btn btn-secondary" onClick={() => setSysImportPreflight(null)}>← Cancel</button>
            <button className="btn btn-primary" onClick={commitSystemImport} disabled={sysImportCommitting}>
              {sysImportCommitting ? <span className="loading-spinner" /> : `✓ Confirm & Import ${rows.length} Records`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── MAIN UPLOAD SCREEN ────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <div className="page-title">📤 Upload Data</div>
        <div className="security-badge">🔒 Files processed in-browser only — never stored raw</div>
      </div>
      <div className="page-content">
        <div className="info-block accent">
          <strong>How it works:</strong> Upload a CSV and review what will change before anything is written to the database.
          {!canUpdate && <span style={{ color: "var(--warning)" }}> As a consultant, CSV uploads create new records only — use the console to edit existing records.</span>}
        </div>
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", marginBottom: "1.25rem", flexWrap: "wrap" }}>
          <label className="form-label" style={{ whiteSpace: "nowrap" }}>Asset Type:</label>
          <select className="form-select" style={{ width: "auto" }} value={uploadType} onChange={e => setUploadType(e.target.value)}>
            <option value="auto">🔍 Auto-detect from headers</option>
            {Object.entries(SCHEMA).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
          </select>
        </div>
        <div className={`upload-zone${dragging ? " drag-over" : ""}`}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); processFile(e.dataTransfer.files[0]); }}
          onClick={() => document.getElementById("upload-file").click()}>
          <input id="upload-file" type="file" accept=".csv" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) { processFile(e.target.files[0]); e.target.value = ""; } }} />
          <div className="upload-icon">📁</div>
          <div className="upload-text"><strong>Click or drag & drop</strong> a CSV file<br /><span style={{ fontSize: "0.76rem" }}>A review screen will appear before anything is saved</span></div>
        </div>
        {lastResult && (
          <div className="info-block success" style={{ marginTop: "1rem" }}>
            ✓ Import complete — <strong>{lastResult.inserted}</strong> inserted, <strong>{lastResult.updated}</strong> updated in <strong>{lastResult.label}</strong>
          </div>
        )}
        {/* SYSTEM IMPORTS SECTION */}
        <div style={{ marginTop: "2rem", marginBottom: "2rem" }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text2)", marginBottom: "0.85rem" }}>System Imports</div>
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "1.25rem" }}>
            <div style={{ fontSize: "0.84rem", color: "var(--text2)", marginBottom: "1rem" }}>
              Import asset data directly from CrowdStrike or Netdata exports. Customer names will be matched automatically — imported records are staged for consultant validation before appearing in reports.
            </div>
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              {/* CrowdStrike */}
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.72rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--warning)", marginBottom: "0.5rem" }}>🛡️ CrowdStrike → Endpoints</div>
                <div className={`upload-zone${sysImportSource === "crowdstrike" ? " drag-over" : ""}`}
                  style={{ padding: "1rem", minHeight: "auto" }}
                  onDragOver={e => { e.preventDefault(); setSysImportSource("crowdstrike"); }}
                  onDragLeave={() => setSysImportSource(null)}
                  onDrop={e => { e.preventDefault(); setSysImportSource(null); processSystemImport(e.dataTransfer.files[0], "crowdstrike"); }}
                  onClick={() => document.getElementById("cs-import-file").click()}>
                  <input id="cs-import-file" type="file" accept=".csv" style={{ display: "none" }}
                    onChange={e => { if (e.target.files[0]) { processSystemImport(e.target.files[0], "crowdstrike"); e.target.value = ""; } }} />
                  <div style={{ textAlign: "center", fontSize: "0.82rem", color: "var(--text2)" }}>
                    <div style={{ fontSize: "1.5rem", marginBottom: "0.3rem" }}>📁</div>
                    Click or drop CrowdStrike CSV export
                  </div>
                </div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", color: "var(--text3)", marginTop: "0.4rem" }}>
                  Columns used: Hostname, Host ID, Customer Name, Last Seen, Platform, OS Version, Type, Local IP
                </div>
              </div>
              {/* Netdata */}
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.72rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--info)", marginBottom: "0.5rem" }}>📡 Netdata → Monitoring Nodes</div>
                <div className={`upload-zone${sysImportSource === "netdata" ? " drag-over" : ""}`}
                  style={{ padding: "1rem", minHeight: "auto" }}
                  onDragOver={e => { e.preventDefault(); setSysImportSource("netdata"); }}
                  onDragLeave={() => setSysImportSource(null)}
                  onDrop={e => { e.preventDefault(); setSysImportSource(null); processSystemImport(e.dataTransfer.files[0], "netdata"); }}
                  onClick={() => document.getElementById("nd-import-file").click()}>
                  <input id="nd-import-file" type="file" accept=".csv" style={{ display: "none" }}
                    onChange={e => { if (e.target.files[0]) { processSystemImport(e.target.files[0], "netdata"); e.target.value = ""; } }} />
                  <div style={{ textAlign: "center", fontSize: "0.82rem", color: "var(--text2)" }}>
                    <div style={{ fontSize: "1.5rem", marginBottom: "0.3rem" }}>📁</div>
                    Click or drop Netdata CSV export
                  </div>
                </div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", color: "var(--text3)", marginTop: "0.4rem" }}>
                  Columns used: agent (hostname + IP), Customer (abbreviation), Type — supports .xlsx and .csv
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: "2rem" }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text2)", marginBottom: "0.85rem" }}>Download Blank Templates</div>
          <div className="template-grid">
            {Object.entries(SCHEMA).map(([key, schema]) => (
              <div key={key} className="template-card" onClick={() => { generateTemplate(key); toast(`Downloaded ${schema.label} template`, "info"); }}>
                <div className="template-card-icon">{schema.icon}</div>
                <div className="template-card-title">{schema.label}</div>
                <div className="template-card-desc">{schema.fields.length} fields · XLSX · Includes Valid Values tab</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// IMPORT ALIAS MANAGER
// ============================================================
function ImportAliasManager({ userEmail, toast }) {
  const [aliases, setAliases]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null); // null | "add" | alias object
  const [form, setForm]         = useState({ alias: "", customer_name: "", source: "netdata", notes: "" });

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("system_import_aliases")
      .select("*")
      .order("source")
      .order("alias");
    if (error) toast("Load failed: " + error.message, "error");
    else setAliases(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setForm({ alias: "", customer_name: "", source: "netdata", notes: "" });
    setModal("add");
  };
  const openEdit = (a) => {
    setForm({ alias: a.alias, customer_name: a.customer_name, source: a.source, notes: a.notes || "" });
    setModal(a);
  };

  const handleSave = async () => {
    if (!form.alias.trim() || !form.customer_name.trim()) {
      toast("Alias and customer name are required", "error"); return;
    }
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const isAdd = modal === "add";

    // For updates: only send fields that the RLS policy allows writing.
    // Do NOT include created_by on updates — it is write-once on insert only.
    const insertPayload = {
      alias:         form.alias.trim().toUpperCase(),
      customer_name: form.customer_name.trim(),
      source:        form.source,
      notes:         form.notes.trim() || null,
      created_by:    userId,
    };
    const updatePayload = {
      alias:         form.alias.trim().toUpperCase(),
      customer_name: form.customer_name.trim(),
      source:        form.source,
      notes:         form.notes.trim() || null,
    };

    let error, data;
    if (isAdd) {
      ({ data, error } = await supabase
        .from("system_import_aliases")
        .insert(insertPayload)
        .select()
        .single());
    } else {
      // .select() after update lets us confirm the row was actually changed
      ({ data, error } = await supabase
        .from("system_import_aliases")
        .update(updatePayload)
        .eq("id", modal.id)
        .select()
        .single());
    }

    if (error) { toast("Save failed: " + error.message, "error"); return; }
    if (!data) {
      // Update matched zero rows — almost always an RLS policy block
      toast("Save blocked — check your permissions or try again", "error");
      return;
    }

    await logAudit(isAdd ? "INSERT" : "UPDATE", "system_import_aliases",
      updatePayload.alias, isAdd ? null : modal, updatePayload, userEmail);
    toast(isAdd ? "Alias added" : "Alias updated", "success");
    setModal(null);
    load();
  };

  const handleDelete = async (a) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Delete alias "${a.alias}" → "${a.customer_name}"?`)) return;
    const { error } = await supabase.from("system_import_aliases").delete().eq("id", a.id);
    if (error) { toast("Delete failed: " + error.message, "error"); return; }
    await logAudit("DELETE", "system_import_aliases", a.alias, a, null, userEmail);
    toast("Alias deleted", "info");
    load();
  };

  const sourceLabel = { netdata: "📡 Netdata", crowdstrike: "🛡️ CrowdStrike", any: "🔀 Any" };
  const sources = [...new Set(aliases.map(a => a.source))];

  return (
    <div>
      <div className="page-header">
        <div className="page-title">🔤 Import Aliases</div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={openAdd}>+ Add Alias</button>
        </div>
      </div>
      <div className="page-content">
        <div className="info-block accent">
          <strong>What are aliases?</strong> When importing from CrowdStrike or Netdata, customer names
          in those systems may not exactly match the names in this CMDB. Aliases map the abbreviation or
          shorthand used in the export (e.g. <code>BROOK</code>, <code>EMS &gt; Rockland</code>) to the
          correct customer name here. Aliases saved here are used automatically on future imports —
          no fuzzy matching needed.
        </div>

        {loading ? (
          <div style={{ textAlign: "center", paddingTop: "3rem" }}>
            <div className="loading-spinner" style={{ width: 36, height: 36, borderWidth: 3 }} />
          </div>
        ) : (
          <>
            {sources.length === 0 && (
              <div style={{ color: "var(--text3)", fontFamily: "var(--font-mono)", fontSize: "0.84rem", padding: "2rem", textAlign: "center" }}>
                No aliases defined yet. Add one above or save mappings during a system import.
              </div>
            )}
            {sources.map(src => (
              <div key={src} style={{ marginBottom: "2rem" }}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text2)", marginBottom: "0.75rem" }}>
                  {sourceLabel[src] || src} — {aliases.filter(a => a.source === src).length} aliases
                </div>
                <div className="table-wrap">
                  <table>
                    <thead><tr>
                      <th>Alias</th>
                      <th>Maps To (Customer Name)</th>
                      <th>Notes</th>
                      <th>Added</th>
                      <th></th>
                    </tr></thead>
                    <tbody>
                      {aliases.filter(a => a.source === src).map(a => (
                        <tr key={a.id}>
                          <td style={{ fontFamily: "var(--font-mono)", fontWeight: 700, color: "var(--accent2)" }}>{a.alias}</td>
                          <td>{a.customer_name}</td>
                          <td style={{ color: "var(--text3)", fontSize: "0.78rem" }}>{a.notes || "—"}</td>
                          <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.72rem", color: "var(--text3)" }}>
                            {a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}
                          </td>
                          <td>
                            <div className="td-actions">
                              <button className="btn btn-secondary btn-sm" onClick={() => openEdit(a)}>Edit</button>
                              <button className="btn btn-danger btn-sm" onClick={() => handleDelete(a)}>Del</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* ADD / EDIT MODAL */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <div className="modal-title">{modal === "add" ? "Add Import Alias" : `Edit Alias: ${modal.alias}`}</div>
              <button className="modal-close" onClick={() => setModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group form-mb">
                <label className="form-label">Source System</label>
                <select className="form-select" value={form.source}
                  onChange={e => setForm(f => ({ ...f, source: e.target.value }))}>
                  <option value="netdata">📡 Netdata</option>
                  <option value="crowdstrike">🛡️ CrowdStrike</option>
                  <option value="any">🔀 Any (applies to all sources)</option>
                </select>
              </div>
              <div className="form-group form-mb">
                <label className="form-label">Alias (abbreviation used in the export)</label>
                <input className="form-input" type="text"
                  placeholder="e.g. BROOK, EMS > Rockland"
                  value={form.alias}
                  onChange={e => setForm(f => ({ ...f, alias: e.target.value.toUpperCase() }))} />
              </div>
              <div className="form-group form-mb">
                <label className="form-label">Customer Name (as it appears in this CMDB)</label>
                <input className="form-input" type="text"
                  placeholder="e.g. Brookdale"
                  value={form.customer_name}
                  onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Notes (optional — e.g. domain name)</label>
                <input className="form-input" type="text"
                  placeholder="e.g. brookdalecc.edu"
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>
                {modal === "add" ? "Add Alias" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// DASHBOARD
// ============================================================
// ============================================================
// STACK VIEW
// ============================================================
function StackView({ toast }) {
  const [customers,    setCustomers]    = useState([]);
  const [custFilter,   setCustFilter]   = useState("");
  const [appFilter,    setAppFilter]    = useState("");
  const [envFilter,    setEnvFilter]    = useState("");
  const [rows,         setRows]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [searched,     setSearched]     = useState(false);

  useEffect(() => {
    supabase.from("customers").select("customer_id,customer_name").eq("active","Yes").order("customer_name")
      .then(({ data }) => setCustomers(data || []));
  }, []);

  const load = async () => {
    setLoading(true); setSearched(true);
    let q = supabase.from("stack_view").select("*");
    if (custFilter)  q = q.eq("customer_id", custFilter);
    if (appFilter)   q = q.eq("application_name", appFilter);
    if (envFilter)   q = q.eq("environment", envFilter);
    const { data, error } = await q.order("customer_name").order("app_display_name").order("environment");
    if (error) { toast("Load failed: " + error.message, "error"); }
    else setRows(data || []);
    setLoading(false);
  };

  const appNames = ["Banner","Colleague","Workflow","Self-Service","Luminis","Degree Works","Ethos","Ellucian Experience","Other"];
  const envNames = ["Prod","Test","Dev","Unknown"];

  const StatusDot = ({ v }) => {
    const color = v === "Active" || v === "Running" ? "var(--success)"
                : v === "Inactive" || v === "Stopped" ? "var(--danger)"
                : "var(--text3)";
    return <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: color, marginRight: 5 }} />;
  };

  return (
    <div>
      <div className="page-header">
        <span style={{ fontSize: "1.8rem" }}>🗂️</span>
        <div className="page-title">Stack View</div>
      </div>
      <div className="page-content">
        {/* Filters */}
        <div className="toolbar" style={{ flexWrap: "wrap", gap: "0.5rem", marginBottom: "1rem" }}>
          <select className="form-select" style={{ width: "auto", minWidth: 220 }}
            value={custFilter} onChange={e => setCustFilter(e.target.value)}>
            <option value="">All Customers</option>
            {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.customer_name}</option>)}
          </select>
          <select className="form-select" style={{ width: "auto", minWidth: 180 }}
            value={appFilter} onChange={e => setAppFilter(e.target.value)}>
            <option value="">All Applications</option>
            {appNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <select className="form-select" style={{ width: "auto", minWidth: 140 }}
            value={envFilter} onChange={e => setEnvFilter(e.target.value)}>
            <option value="">All Environments</option>
            {envNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <button className="btn btn-primary" onClick={load} disabled={loading}>
            {loading ? <span className="loading-spinner" /> : "🔍 Search"}
          </button>
        </div>

        {/* Results */}
        {!searched && (
          <div className="empty-state" style={{ marginTop: "3rem" }}>
            <div className="empty-icon">🗂️</div>
            <div className="empty-text">Choose filters above and click Search<br/>to view the application stack across customers.</div>
          </div>
        )}

        {searched && !loading && rows.length === 0 && (
          <div className="empty-state" style={{ marginTop: "2rem" }}>
            <div className="empty-icon">📭</div>
            <div className="empty-text">No applications match your filters.<br/>Add application records on the Applications page first.</div>
          </div>
        )}

        {rows.length > 0 && (
          <>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.74rem", color: "var(--text3)", marginBottom: "0.75rem" }}>
              {rows.length} application instance{rows.length !== 1 ? "s" : ""} found
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {rows.map((r, i) => (
                <div key={i} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden" }}>
                  {/* App header */}
                  <div style={{ padding: "0.85rem 1.25rem", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "1.1rem" }}>📦</span>
                    <div style={{ fontWeight: 700, fontSize: "1rem" }}>{r.app_display_name}</div>
                    {r.app_version && <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.78rem", background: "var(--bg3)", padding: "0.15rem 0.6rem", borderRadius: 20, color: "var(--accent2)" }}>v{r.app_version}</span>}
                    <EnvBadge val={r.environment} />
                    {r.app_status && <span style={{ display: "flex", alignItems: "center", fontFamily: "var(--font-mono)", fontSize: "0.74rem", color: "var(--text2)" }}><StatusDot v={r.app_status} />{r.app_status}</span>}
                    <div style={{ marginLeft: "auto", fontFamily: "var(--font-mono)", fontSize: "0.76rem", color: "var(--text2)" }}>
                      {r.customer_name}
                    </div>
                    {r.management_scope && <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.72rem", color: "var(--text3)" }}>Scope: {r.management_scope}</span>}
                  </div>

                  {/* Stack layers */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 0 }}>
                    {/* Server */}
                    <div style={{ padding: "0.85rem 1.25rem", borderRight: "1px solid var(--border)" }}>
                      <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text3)", marginBottom: "0.5rem" }}>🖥️ Server</div>
                      {r.server_name
                        ? <>
                            <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>{r.server_name}</div>
                            {r.os && <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.74rem", color: "var(--text2)" }}>{r.os}{r.os_version ? ` ${r.os_version}` : ""}</div>}
                            {r.local_ip && <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.72rem", color: "var(--text3)" }}>{r.local_ip}</div>}
                          </>
                        : <div style={{ color: "var(--text3)", fontSize: "0.82rem", fontStyle: "italic" }}>Not linked</div>
                      }
                    </div>

                    {/* Middleware */}
                    <div style={{ padding: "0.85rem 1.25rem", borderRight: "1px solid var(--border)" }}>
                      <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text3)", marginBottom: "0.5rem" }}>⚙️ Middleware</div>
                      {r.middleware_name
                        ? <>
                            <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>{r.middleware_name}</div>
                            {r.middleware_type && <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.74rem", color: "var(--text2)" }}>{r.middleware_type}{r.middleware_version ? ` v${r.middleware_version}` : ""}</div>}
                            {r.middleware_port && <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.72rem", color: "var(--text3)" }}>:{r.middleware_port}</div>}
                          </>
                        : <div style={{ color: "var(--text3)", fontSize: "0.82rem", fontStyle: "italic" }}>Not linked</div>
                      }
                    </div>

                    {/* Database */}
                    <div style={{ padding: "0.85rem 1.25rem" }}>
                      <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text3)", marginBottom: "0.5rem" }}>🗄️ Database</div>
                      {r.database_name
                        ? <>
                            <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>{r.database_name}</div>
                            {r.db_engine && <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.74rem", color: "var(--text2)" }}>{r.db_engine}</div>}
                          </>
                        : <div style={{ color: "var(--text3)", fontSize: "0.82rem", fontStyle: "italic" }}>Not linked</div>
                      }
                    </div>
                  </div>

                  {/* App ID + last updated footer */}
                  <div style={{ padding: "0.4rem 1.25rem", borderTop: "1px solid var(--border)", display: "flex", gap: "1.5rem", fontFamily: "var(--font-mono)", fontSize: "0.68rem", color: "var(--text3)" }}>
                    <span>{r.application_id}</span>
                    {r.last_updated && <span>Updated: {r.last_updated}</span>}
                    {r.import_status === "staged" && <span style={{ color: "var(--warning)" }}>⏳ Staged</span>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Dashboard({ profile }) {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("executive_rollup").select("*").single();
      setStats(data);
      // Compute alerts
      const a = [];
      const { data: unmon } = await supabase.from("servers").select("server_id,server_name,customer_id").eq("monitoring_enabled", "N").limit(10);
      (unmon || []).forEach(s => a.push({ type: "warning", msg: `Unmonitored server: ${s.server_name || s.server_id} (${s.customer_id})` }));
      const { data: alerts_ } = await supabase.from("endpoints").select("endpoint_id,endpoint_name,customer_id").eq("alert_triggered", "Y").limit(10);
      (alerts_ || []).forEach(e => a.push({ type: "danger", msg: `Active alert: ${e.endpoint_name || e.endpoint_id} (${e.customer_id})` }));
      const { data: backups } = await supabase.from("databases").select("database_id,database_name").eq("backup_status", "Issue").limit(10);
      (backups || []).forEach(d => a.push({ type: "danger", msg: `Backup issue: ${d.database_name || d.database_id}` }));
      setAlerts(a);
      setLoading(false);
    })();
  }, []);

  const statItems = stats ? [
    { icon: "🏢", label: "Customers", value: stats.total_customers, sub: `${stats.active_customers} active`, color: "#6366f1" },
    { icon: "🖥️", label: "Servers", value: stats.total_servers, sub: `${stats.monitored_servers} monitored · ${stats.cs_servers} CS`, color: "#0ea5e9" },
    { icon: "🗄️", label: "Databases", value: stats.total_databases, sub: `${stats.healthy_backups} healthy backups · ${stats.dr_databases} DR`, color: "#10b981" },
    { icon: "🛡️", label: "Endpoints", value: stats.total_endpoints, sub: `${stats.cs_endpoints} w/ CrowdStrike`, color: "#f59e0b" },
    { icon: "📡", label: "Nodes", value: stats.total_nodes, sub: "monitoring nodes", color: "#ec4899" },
    { icon: "🔗", label: "Network",      value: stats.total_network_assets, sub: "network assets",    color: "#8b5cf6" },
    { icon: "⚙️", label: "Middleware",   value: stats.total_middleware,      sub: "middleware instances", color: "#f97316" },
    { icon: "📦", label: "Applications", value: stats.total_applications,    sub: "app instances",        color: "#14b8a6" },
  ] : [];

  if (loading) return <div className="page-content" style={{ textAlign: "center", paddingTop: "4rem" }}><div className="loading-spinner" style={{ width: 40, height: 40, borderWidth: 3 }} /></div>;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">📊 Executive Rollup</div>
        {(profile?.role === "administrator" || profile?.role === "manager") && <div className="page-actions"><span style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem", color: "var(--text2)" }}>{stats?.active_users} active users · {stats?.pending_users} pending approval</span></div>}
      </div>
      <div className="page-content">
        <div className="stats-grid">
          {statItems.map((s, i) => (
            <div key={i} className="stat-card" style={{ borderTop: `2px solid ${s.color}` }}>
              <div className="stat-icon">{s.icon}</div>
              <div className="stat-value" style={{ color: s.color }}>{s.value ?? "—"}</div>
              <div className="stat-label">{s.label}</div>
              <div className="stat-sub">{s.sub}</div>
            </div>
          ))}
        </div>
        {alerts.length > 0 && (
          <div style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "var(--radius)", padding: "1.25rem", marginBottom: "1.5rem" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--danger)", marginBottom: "0.75rem" }}>⚠ Attention Required ({alerts.length})</div>
            {alerts.map((a, i) => (
              <div key={i} style={{ fontFamily: "var(--font-mono)", fontSize: "0.8rem", color: a.type === "danger" ? "var(--danger)" : "var(--warning)", padding: "0.3rem 0", borderTop: i > 0 ? "1px solid rgba(239,68,68,0.12)" : "none" }}>{a.msg}</div>
            ))}
          </div>
        )}
        {stats && (
          <div className="two-col">
            <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "1.25rem" }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text2)", marginBottom: "1rem" }}>Security Coverage</div>
              {[
                { label: "Servers Monitored", val: stats.monitored_servers, total: stats.total_servers, color: "#0ea5e9" },
                { label: "Servers w/ CrowdStrike", val: stats.cs_servers, total: stats.total_servers, color: "#6366f1" },
                { label: "Endpoints w/ CrowdStrike", val: stats.cs_endpoints, total: stats.total_endpoints, color: "#f59e0b" },
                { label: "Databases w/ Healthy Backup", val: stats.healthy_backups, total: stats.total_databases, color: "#10b981" },
                { label: "Databases w/ DR", val: stats.dr_databases, total: stats.total_databases, color: "#ec4899" },
              ].map((item, i) => (
                <div key={i} className="chart-bar-wrap">
                  <div className="chart-bar-label"><span>{item.label}</span><span>{item.val}/{item.total} ({item.total ? Math.round((item.val/item.total)*100) : 0}%)</span></div>
                  <div className="chart-bar"><div className="chart-bar-fill" style={{ width: item.total ? `${(item.val/item.total)*100}%` : "0%", background: item.color }} /></div>
                </div>
              ))}
            </div>
            <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "1.25rem" }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text2)", marginBottom: "1rem" }}>Asset Summary</div>
              {[
                { label: "Total Assets", val: (stats.total_servers||0)+(stats.total_databases||0)+(stats.total_endpoints||0)+(stats.total_nodes||0)+(stats.total_network_assets||0)+(stats.total_middleware||0)+(stats.total_applications||0), color: "var(--text)" },
                { label: "Active Customers", val: stats.active_customers, color: "#6366f1" },
                { label: "Active Security Alerts", val: stats.active_alerts, color: stats.active_alerts > 0 ? "var(--danger)" : "var(--success)" },
                { label: "Active CMDB Users", val: stats.active_users, color: "var(--success)" },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.55rem 0", borderBottom: "1px solid var(--border)" }}>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.8rem", color: "var(--text2)" }}>{item.label}</span>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: "1rem", fontWeight: 700, color: item.color }}>{item.val ?? "—"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// ADMIN: USER MANAGEMENT
// ============================================================
function UserManagement({ userEmail, toast }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from("profiles").select("*").order("created_at");
    setUsers(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const updateUser = async (id, updates) => {
    const { error } = await supabase.from("profiles").update({ ...updates, approved_by: (await supabase.auth.getUser()).data.user?.id, approved_at: new Date().toISOString() }).eq("id", id);
    if (error) toast("Update failed: " + error.message, "error");
    else { toast("User updated", "success"); load(); }
  };

  const pending = users.filter(u => !u.is_active || u.role === "pending");
  const active = users.filter(u => u.is_active && u.role !== "pending");

  return (
    <div>
      <div className="page-header"><div className="page-title">👥 User Management</div></div>
      <div className="page-content">
        {pending.length > 0 && (
          <>
            <div className="info-block warning" style={{ marginBottom: "1.25rem" }}>
              <strong>{pending.length} user{pending.length > 1 ? "s" : ""} awaiting approval.</strong> Review and approve or deny access below.
            </div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text2)", marginBottom: "0.75rem" }}>Pending Approval</div>
            {pending.map(u => (
              <div key={u.id} className="user-card">
                <div className="user-avatar">{(u.full_name || u.email || "?")[0].toUpperCase()}</div>
                <div className="user-info">
                  <div className="user-name">{u.full_name || "—"}</div>
                  <div className="user-email">{u.email} · Registered {new Date(u.created_at).toLocaleDateString()}</div>
                </div>
                <div className="user-actions">
                  <button className="btn btn-success btn-sm" onClick={() => updateUser(u.id, { role: "active_consultant", is_active: true })}>✓ Approve</button>
                  <button className="btn btn-danger btn-sm" onClick={() => updateUser(u.id, { is_active: false, role: "pending" })}>✗ Deny</button>
                </div>
              </div>
            ))}
            <div className="divider" />
          </>
        )}
        <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text2)", marginBottom: "0.75rem" }}>Active Users ({active.length})</div>
        {loading ? <div style={{ textAlign: "center" }}><div className="loading-spinner" /></div> : active.map(u => (
          <div key={u.id} className="user-card">
            <div className="user-avatar">{(u.full_name || u.email || "?")[0].toUpperCase()}</div>
            <div className="user-info">
              <div className="user-name">{u.full_name || "—"} {u.email === userEmail && <span style={{ fontSize: "0.72rem", color: "var(--text2)" }}>(you)</span>}</div>
              <div className="user-email">{u.email} · Last sign in: {u.last_sign_in ? new Date(u.last_sign_in).toLocaleDateString() : "Never"}</div>
            </div>
            <div className="user-actions" style={{ alignItems: "center", gap: "0.75rem" }}>
              <RoleBadge role={u.role} />
              {u.email !== userEmail && (
                <>
                  {u.role !== "administrator" && <button className="btn btn-secondary btn-sm" onClick={() => updateUser(u.id, { role: "administrator" })}>Make Admin</button>}
                  {u.role !== "manager" && u.role !== "administrator" && <button className="btn btn-secondary btn-sm" onClick={() => updateUser(u.id, { role: "manager" })}>Make Manager</button>}
                  {(u.role === "administrator" || u.role === "manager") && <button className="btn btn-secondary btn-sm" onClick={() => updateUser(u.id, { role: "active_consultant" })}>Demote</button>}
                  <button className="btn btn-danger btn-sm" onClick={() => updateUser(u.id, { is_active: false })}>Revoke</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// ADMIN: EXPORT CENTER
// ============================================================
function ExportCenter({ userEmail, toast }) {
  const [loading, setLoading] = useState({});
  const [exportHistory, setExportHistory] = useState([]);

  useEffect(() => {
    supabase.from("export_log").select("*").order("created_at", { ascending: false }).limit(20)
      .then(({ data }) => setExportHistory(data || []));
  }, []);

  const exportTable = async (type, all = false) => {
    setLoading(l => ({ ...l, [type]: true }));
    const schema = type === "all" ? null : SCHEMA[type];
    let allData = {};
    const tables = type === "all" ? Object.entries(SCHEMA) : [[type, schema]];
    for (const [t, s] of tables) {
      const { data } = await supabase.from(s.table).select("*");
      allData[t] = data || [];
    }
    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (type === "all") {
      // Multi-sheet ZIP-like: one CSV per table, all in a combined file with separators
      let combined = "";
      let totalCount = 0;
      for (const [t, s] of Object.entries(SCHEMA)) {
        const rows = allData[t] || [];
        const headers = s.fields.map(f => f.key);
        combined += `\n\n=== ${s.label.toUpperCase()} (${rows.length} records) ===\n`;
        combined += toCSV(headers, rows);
        totalCount += rows.length;
      }
      const blob = new Blob([combined], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `EMS_CMDB_Full_Export_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      await supabase.from("export_log").insert({ exported_by: userId, exported_by_email: userEmail, export_type: "full", record_count: totalCount });
      await logAudit("EXPORT_FULL", "all", "bulk", null, { total_records: totalCount }, userEmail);
      toast(`Full export: ${totalCount} total records`, "success");
    } else {
      const rows = allData[type] || [];
      const headers = schema.fields.map(f => f.key);
      downloadCSV(`EMS_${schema.label}_Export_${new Date().toISOString().split("T")[0]}.csv`, headers, rows);
      await supabase.from("export_log").insert({ exported_by: userId, exported_by_email: userEmail, export_type: type, record_count: rows.length });
      await logAudit("EXPORT", schema.table, "bulk", null, { count: rows.length }, userEmail);
      toast(`Exported ${rows.length} records`, "success");
    }
    setLoading(l => ({ ...l, [type]: false }));
    const { data } = await supabase.from("export_log").select("*").order("created_at", { ascending: false }).limit(20);
    setExportHistory(data || []);
  };

  return (
    <div>
      <div className="page-header"><div className="page-title">⬇ Export Center</div><div className="security-badge">🔒 All exports are audit-logged</div></div>
      <div className="page-content">
        <div className="info-block accent">
          <strong>Smartsheet / External Tool Integration:</strong> Export any dataset as CSV and import directly into Smartsheet, Power BI, Excel, Tableau, or any tool that accepts CSV. For automated pipelines, use the Supabase REST API or a scheduled Edge Function to push data to your BI tool. All exports are logged with user, timestamp, and record count.
        </div>
        <div style={{ marginBottom: "1.5rem" }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text2)", marginBottom: "0.85rem" }}>Full Export</div>
          <div className="export-card" style={{ gridColumn: "1 / -1", flexDirection: "row", alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <div className="export-card-title">🗂️ Complete CMDB Dataset</div>
              <div className="export-card-desc">All {Object.keys(SCHEMA).length} asset types in a single CSV file with section headers. Import into Smartsheet, Power BI, or Excel.</div>
            </div>
            <button className="btn btn-primary" onClick={() => exportTable("all")} disabled={loading["all"]}>
              {loading["all"] ? <span className="loading-spinner" /> : "⬇ Export All"}
            </button>
          </div>
        </div>
        <div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text2)", marginBottom: "0.85rem" }}>Export by Asset Type</div>
          <div className="export-grid">
            {Object.entries(SCHEMA).map(([key, schema]) => (
              <div key={key} className="export-card">
                <div className="export-card-title">{schema.icon} {schema.label}</div>
                <div className="export-card-desc">All fields including metadata (created_by, updated_at)</div>
                <button className="btn btn-secondary btn-sm" onClick={() => exportTable(key)} disabled={loading[key]} style={{ marginTop: "auto" }}>
                  {loading[key] ? <span className="loading-spinner" /> : "⬇ Export CSV"}
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="divider" />
        <div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text2)", marginBottom: "0.85rem" }}>Recent Export History</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date</th><th>By</th><th>Type</th><th>Records</th></tr></thead>
              <tbody>
                {exportHistory.length === 0
                  ? <tr><td colSpan={4} style={{ textAlign: "center", color: "var(--text2)" }}>No exports yet</td></tr>
                  : exportHistory.map(e => (
                    <tr key={e.id}>
                      <td>{new Date(e.created_at).toLocaleString()}</td>
                      <td style={{ maxWidth: 180 }}>{e.exported_by_email}</td>
                      <td><span className="badge badge-blue">{e.export_type}</span></td>
                      <td>{e.record_count?.toLocaleString()}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// ADMIN: AUDIT LOG VIEWER
// ============================================================
function AuditLogPage({ toast }) {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await supabase.from("audit_log").select("*").order("created_at", { ascending: false }).limit(200);
      setLog(data || []);
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!filter) return log;
    const q = filter.toLowerCase();
    return log.filter(r => Object.values(r).some(v => v && String(v).toLowerCase().includes(q)));
  }, [log, filter]);

  const actionColor = a => ({ INSERT:"badge-green", UPDATE:"badge-blue", DELETE:"badge-red", EXPORT:"badge-purple", EXPORT_FULL:"badge-purple", BULK_IMPORT:"badge-yellow", LOGIN:"badge-gray" }[a] || "badge-gray");

  return (
    <div>
      <div className="page-header"><div className="page-title">🔍 Audit Log</div></div>
      <div className="page-content">
        <div className="toolbar">
          <input className="search-input" placeholder="Filter by user, table, action..." value={filter} onChange={e => setFilter(e.target.value)} />
          <span className="record-count">{filtered.length} entries</span>
        </div>
        {loading ? <div style={{ textAlign: "center" }}><div className="loading-spinner" /></div> : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Timestamp</th><th>User</th><th>Action</th><th>Table</th><th>Record ID</th></tr></thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id}>
                    <td style={{ whiteSpace: "nowrap" }}>{new Date(r.created_at).toLocaleString()}</td>
                    <td style={{ maxWidth: 160 }}>{r.user_email}</td>
                    <td><span className={`badge ${actionColor(r.action)}`}>{r.action}</span></td>
                    <td>{r.table_name || "—"}</td>
                    <td>{r.record_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// SETTINGS
// ============================================================
function SettingsPage({ profile, toast }) {
  const [tab, setTab] = useState("account");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  const changePw = async () => {
    if (newPw !== newPw2) { toast("Passwords don't match", "error"); return; }
    if (newPw.length < 12) { toast("Min 12 characters", "error"); return; }
    setPwLoading(true);
    const { error } = await supabase.auth.updateUser({ password: newPw });
    if (error) toast("Error: " + error.message, "error");
    else { toast("Password updated", "success"); setNewPw(""); setNewPw2(""); }
    setPwLoading(false);
  };

  return (
    <div>
      <div className="page-header"><div className="page-title">⚙️ Settings</div></div>
      <div className="page-content" style={{ maxWidth: 680 }}>
        <div className="tab-bar">
          <div className={`tab-item${tab === "account" ? " active" : ""}`} onClick={() => setTab("account")}>Account</div>
          <div className={`tab-item${tab === "security" ? " active" : ""}`} onClick={() => setTab("security")}>Security</div>
          <div className={`tab-item${tab === "info" ? " active" : ""}`} onClick={() => setTab("info")}>Architecture</div>
        </div>
        {tab === "account" && (
          <div className="settings-section">
            <div className="settings-section-title">👤 Account Information</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.82rem", color: "var(--text2)", marginBottom: "1rem", display: "grid", gap: "0.5rem" }}>
              <div><strong style={{ color: "var(--text)" }}>Name:</strong> {profile?.full_name || "—"}</div>
              <div><strong style={{ color: "var(--text)" }}>Email:</strong> {profile?.email}</div>
              <div><strong style={{ color: "var(--text)" }}>Role:</strong> <RoleBadge role={profile?.role} /></div>
              <div><strong style={{ color: "var(--text)" }}>MFA:</strong> <span className="badge badge-green">Enrolled</span></div>
            </div>
            <div className="divider" />
            <div style={{ fontWeight: 700, marginBottom: "0.75rem" }}>Change Password</div>
            <div className="form-group form-mb">
              <label className="form-label">New Password</label>
              <input className="form-input" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Min 12 characters" />
            </div>
            <div className="form-group form-mb">
              <label className="form-label">Confirm New Password</label>
              <input className="form-input" type="password" value={newPw2} onChange={e => setNewPw2(e.target.value)} />
            </div>
            <button className="btn btn-primary" onClick={changePw} disabled={pwLoading}>{pwLoading ? <span className="loading-spinner" /> : "Update Password"}</button>
          </div>
        )}
        {tab === "security" && (
          <div className="settings-section">
            <div className="settings-section-title">🔒 Security Summary</div>
            <div style={{ display: "grid", gap: "0.75rem", fontFamily: "var(--font-mono)", fontSize: "0.82rem" }}>
              {[
                { label: "Transport Encryption", value: "TLS 1.3 (end-to-end enforced)", ok: true },
                { label: "Data at Rest", value: "AES-256 (encrypted cloud database)", ok: true },
                { label: "Authentication", value: "Email + Password + TOTP MFA", ok: true },
                { label: "Session Management", value: "PKCE flow, auto-refresh, JWT", ok: true },
                { label: "Access Control", value: "Row Level Security (RLS) — DB-enforced", ok: true },
                { label: "Audit Trail", value: "Full audit log with user, action, record", ok: true },
                { label: "File Upload Safety", value: "Browser-only parsing, never raw-stored", ok: true },
                { label: "Delete Permissions", value: "Administrator only", ok: true },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem 0", borderBottom: "1px solid var(--border)" }}>
                  <span style={{ color: "var(--text2)" }}>{item.label}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span style={{ color: "var(--text)", textAlign: "right" }}>{item.value}</span>
                    <span style={{ color: item.ok ? "var(--success)" : "var(--danger)" }}>{item.ok ? "✓" : "✗"}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "info" && (
          <div className="settings-section">
            <div className="settings-section-title">🏗️ Architecture</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.8rem", color: "var(--text2)", lineHeight: 1.8 }}>
              {[
                ["Frontend", "React (static) — deployable to Cloudflare Pages, Netlify, or GitHub Pages ($0)"],
                ["Backend / DB", "PostgreSQL — Row Level Security enforced at DB layer"],
                ["Auth", "Cloud Auth — PKCE, JWT, MFA TOTP"],
                ["Encryption (transit)", "TLS 1.3 — enforced by cloud infrastructure"],
                ["Encryption (rest)", "AES-256 — managed encrypted database"],
                ["Access control", "RLS policies — enforced at DB level, not app level"],
                ["Capacity", "500MB on free tier (~10M+ rows depending on row size)"],
                ["Cost", "Cloud hosted: $0/month up to 50,000 users, 500MB storage"],
                ["Scale path", "Pro tier: $25/month for 8GB storage + unlimited users"],
                ["Smartsheet", "CSV export → Smartsheet import / Supabase REST API for live feeds"],
              ].map(([k, v]) => (
                <div key={k} style={{ paddingBottom: "0.5rem", marginBottom: "0.5rem", borderBottom: "1px solid var(--border)" }}>
                  <strong style={{ color: "var(--text)" }}>{k}:</strong> {v}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================
export default function App() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [page, setPage] = useState("dashboard");
  const { toasts, toast } = useToast();
  const suppressAuthListener = useRef(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (suppressAuthListener.current) return;
      setSession(session);
      if (session) loadProfile(session.user.id);
      else setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (suppressAuthListener.current) return;
      setSession(session);
      if (session) loadProfile(session.user.id);
      else { setProfile(null); setAuthLoading(false); }
    });
    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (userId, force = false) => {
    if (!force && suppressAuthListener.current) return;
    const { data } = await supabase.from("profiles").select("*").eq("id", userId).single();
    if (!force && suppressAuthListener.current) return;
    setProfile(data);
    setAuthLoading(false);
    if (data && !data.is_active) {
      toast("Your account is pending administrator approval", "warning");
      await supabase.auth.signOut();
    }
  };

  const handleSignIn = (newSession) => {
    setSession(newSession);
    setAuthLoading(false);
    if (newSession?.user?.id) loadProfile(newSession.user.id, true);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setPage("dashboard");
    toast("Signed out", "info");
  };

  if (authLoading) return (
    <>
      <style>{styles}</style>
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)" }}>
        <div className="loading-spinner" style={{ width: 40, height: 40, borderWidth: 3 }} />
      </div>
    </>
  );

  if (!session || !profile) return (<><style>{styles}</style><AuthScreen toast={toast} suppressAuthListener={suppressAuthListener} onSignIn={handleSignIn} /><ToastContainer toasts={toasts} /></>);

  const recertInfo = getRecertStatusInfo(profile);

  // Gate: show suspension screen if recert suspended
  if (recertInfo.isSuspended) {
    return (
      <>
        <style>{styles}</style>
        <RecertSuspendedScreen
          profile={profile}
          supabase={supabase}
          onSignOut={signOut}
        />
        <ToastContainer toasts={toasts} />
      </>
    );
  }

  const isAdmin = profile.role === "administrator";
  const isManager = profile.role === "manager";
  const canManage = isAdmin || isManager; // edit/delete rights short-hand
  const userEmail = profile.email;

  const navGroups = [
    {
      label: "Main",
      items: [
        { id: "dashboard", label: "Dashboard", icon: "📊" },
        { id: "upload", label: "Upload Data", icon: "📤" },
      ]
    },
    {
      label: "Account Management",
      items: [
        { id: "engagements", label: "Engagements", icon: "📋" },
        { id: "stackview",   label: "Stack View",  icon: "🗂️" },
      ]
    },
    {
      label: "Asset Types",
      items: Object.entries(SCHEMA).map(([key, s]) => ({ id: key, label: s.label, icon: s.icon }))
    },
    ...(isAdmin ? [{
      label: "Administration",
      items: [
        { id: "users",   label: "User Management", icon: "👥" },
        { id: "aliases", label: "Import Aliases",  icon: "🔤" },
        { id: "recert",  label: "Recertification", icon: "🔁" },
        { id: "exports", label: "Export Center",   icon: "⬇" },
        { id: "audit",   label: "Audit Log",       icon: "🔍" },
      ]
    }] : isManager ? [{
      label: "Administration",
      items: [
        { id: "users",   label: "User Management", icon: "👥" },
        { id: "aliases", label: "Import Aliases",  icon: "🔤" },
        { id: "recert",  label: "Recertification", icon: "🔁" },
        { id: "audit",   label: "Audit Log",       icon: "🔍" },
      ]
    }] : []),
    {
      label: "Account",
      items: [{ id: "settings", label: "Settings", icon: "⚙️" }]
    }
  ];

  // Counts: we'll need to fetch these separately or pass them down
  // For sidebar we just show the page name for now

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        <aside className="sidebar">
          <div className="sidebar-head">
            <div className="sidebar-logo">EMS CMDB</div>
            <div className="sidebar-sub">Secured · MFA Required</div>
          </div>
          <div style={{ padding: "0 0.75rem", marginBottom: "0.5rem" }}>
            <div className="sidebar-user">
              <div className="sidebar-user-name">{profile.full_name || profile.email}</div>
              <div className="sidebar-user-role"><RoleBadge role={profile.role} /></div>
            </div>
          </div>
          {navGroups.map(group => (
            <div key={group.label}>
              <div className="sidebar-section">{group.label}</div>
              {group.items.map(item => (
                <div key={item.id} className={`sidebar-item${page === item.id ? " active" : ""}`} onClick={() => setPage(item.id)}>
                  {item.icon} {item.label}
                </div>
              ))}
            </div>
          ))}
          <div className="sidebar-footer">
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", color: "var(--text3)", textAlign: "center", marginBottom: "0.4rem" }}>
              🔒 TLS 1.3 · AES-256 · MFA
            </div>
            <button className="sidebar-lock-btn" onClick={signOut}>Sign Out</button>
          </div>
        </aside>

        <main className="main">
          <RecertWarningBanner profile={profile} supabase={supabase} />
          {page === "dashboard" && <Dashboard profile={profile} />}
          {page === "upload" && <UploadPage profile={profile} userEmail={userEmail} toast={toast} />}
          {Object.keys(SCHEMA).includes(page) && <AssetPage type={page} profile={profile} userEmail={userEmail} toast={toast} canManage={canManage} />}
          {page === "users"   && (isAdmin || isManager) && <UserManagement userEmail={userEmail} toast={toast} />}
          {page === "aliases" && (isAdmin || isManager) && <ImportAliasManager userEmail={userEmail} toast={toast} />}
          {page === "exports" && isAdmin && <ExportCenter userEmail={userEmail} toast={toast} />}
          {page === "audit" && (isAdmin || isManager) && <AuditLogPage toast={toast} />}
          {page === "recert" && (isAdmin || isManager) && (
            <RecertManagementPage
              supabase={supabase}
              userEmail={userEmail}
              toast={toast}
            />
          )}
          {page === "engagements" && (
            <EngagementPage
              supabase={supabase}
              profile={profile}
              userEmail={userEmail}
              toast={toast}
              canManage={canManage}
            />
          )}
          {page === "settings" && <SettingsPage profile={profile} toast={toast} />}
          {page === "stackview" && <StackView toast={toast} />}
        </main>
      </div>
      <ToastContainer toasts={toasts} />
    </>
  );
}
